import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useBlocker } from 'react-router-dom';
import {
  Box, Paper, Typography, TextField, FormControl, InputLabel, Select, MenuItem,
  Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Grid, Alert, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Divider, Checkbox, ListItemText, Snackbar, Chip, Menu, Collapse, Tooltip,
  Popover, FormGroup, FormControlLabel
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SettingsIcon from '@mui/icons-material/Settings';
import LockIcon from '@mui/icons-material/Lock';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from '../api/client';
import { validateRules, BASE_RULE_SETS, VENDOR_DEFAULT_RULE_SETS, detectRuleSetConflicts } from '../utils/GroupingRules';
import RuleConfigDialog from './RuleConfigDialog';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8004';
const VENDORS = [
  { value: 'american_eagle',      label: 'American Eagle'         },
  { value: 'american_eagle_mens', label: 'American Eagle Men'     },
  { value: 'aerie',               label: 'Aerie'                  },
  { value: 'lane_bryant',         label: 'Lane Bryant'            },
  { value: 'loft',                label: 'Ann Taylor Loft'        },
  { value: 'loft_outlet',         label: 'Ann Taylor Loft Outlet' },
  { value: 'ann_taylor',          label: 'Ann Taylor'             },
  { value: 'ann_taylor_factory',  label: 'Ann Taylor Factory'     },
];

// Sub-vendor tags — maps DB key to display identity
const SUB_VENDOR_TAG = {
  // snake_case keys (legacy pre-migration rows)
  american_eagle_mens: { label: 'AEM',  full: 'American Eagle Men',     color: '#a01025' },
  aerie:               { label: 'AR',   full: 'Aerie',                  color: '#e85d8a' },
  pink:                { label: 'PK',   full: 'Pink',                   color: '#db2777' },
  loft_outlet:         { label: 'LFO',  full: 'Ann Taylor Loft Outlet', color: '#92400e' },
  ann_taylor:          { label: 'AT',   full: 'Ann Taylor',             color: '#78350f' },
  ann_taylor_factory:  { label: 'ATF',  full: 'Ann Taylor Factory',     color: '#6b3a0f' },
  // customerCode keys (post-migration rows)
  BAM:  { label: 'AEM',  full: 'American Eagle Men',     color: '#a01025' },
  BAR:  { label: 'AR',   full: 'Aerie',                  color: '#e85d8a' },
  BVP:  { label: 'PK',   full: 'Pink',                   color: '#db2777' },
  BLFO: { label: 'LFO',  full: 'Ann Taylor Loft Outlet', color: '#92400e' },
  BAT:  { label: 'AT',   full: 'Ann Taylor',             color: '#78350f' },
  BATF: { label: 'ATF',  full: 'Ann Taylor Factory',     color: '#6b3a0f' },
};

// Sub-vendor → parent vendor alias for rule-set loading.
// Sub-vendors share the same grouping rules as their parent.
const RULE_VENDOR_ALIAS = {
  american_eagle_mens: 'american_eagle',
  aerie:               'american_eagle',
  loft_outlet:         'loft',
  ann_taylor:          'loft',
  ann_taylor_factory:  'loft',
};

// Parent vendor display names — covers both legacy snake_case and post-migration customerCodes
const PARENT_VENDOR_LABEL = {
  // snake_case (legacy)
  american_eagle: 'American Eagle',
  lane_bryant:    'Lane Bryant',
  loft:           'Ann Taylor Loft',
  mast:           "Victoria's Secret",
  chico:          "Chico's",
  freepeople:     'Free People',
  talbots:        'Talbots',
  // customerCode (post-migration)
  BAE:  'American Eagle',
  BLB:  'Lane Bryant',
  BLF:  'Ann Taylor Loft',
  BVS:  "Victoria's Secret",
  BC:   "Chico's",
  BFP:  'Free People',
  BTL:  'Talbots',
};

// Returns the human-readable vendor name for any DB vendor key,
// preferring the sub-vendor's own full name when applicable.
const vendorDisplayName = (key) =>
  SUB_VENDOR_TAG[key]?.full || PARENT_VENDOR_LABEL[key] || key;

/**
 * Which date field to pre-select when a vendor is chosen.
 * All listed vendors primarily filter by initial_order_date;
 * MAST uses dc_date as its primary date dimension.
 */
const VENDOR_DEFAULT_DATE_FIELD = {
  american_eagle:      'initial_order_date',
  american_eagle_mens: 'initial_order_date',
  aerie:               'initial_order_date',
  loft:                'initial_order_date',
  loft_outlet:         'initial_order_date',
  ann_taylor:          'initial_order_date',
  ann_taylor_factory:  'initial_order_date',
  talbots:             'initial_order_date',
  lane_bryant:         'initial_order_date',
  freepeople:          'initial_order_date',
  chico:               'initial_order_date',
  mast:                'dc_date',
};

/**
 * Vendor-specific display labels for date fields in the dropdown.
 * Each vendor's PO documents use their own terminology for the same DB column.
 * Format: { [vendorValue]: { [fieldKey]: 'Label shown in UI' } }
 */
const VENDOR_DATE_FIELD_LABELS = {
  american_eagle: { initial_order_date: 'Initial Order Date' },
  loft:           { initial_order_date: 'Initial Date'        },
  talbots:        { initial_order_date: 'Order Date'          },
  lane_bryant:    { initial_order_date: 'PO Date #'           },
  freepeople:     { initial_order_date: 'Order Date'          },
  chico:          { initial_order_date: 'PO Date'             },
};
// All possible fields across all vendors (header + line item, verified against main_v2.py)
const FIELDS = [
  // ── PO header fields ──
  { key: 'customer_po_number',                  label: 'PO #' },
  { key: 'sub_po',                              label: 'Sub PO' },
  { key: 'customer_distribution_channel',       label: 'Distribution Channel' },
  { key: 'initial_order_date',                  label: 'Order Date',         type: 'date' },
  { key: 'po_version_number',                   label: 'PO Version No' },
  { key: 'po_version_date',                     label: 'PO Version Date',    type: 'date' },
  { key: 'bshipdate',                           label: 'Ship Date (BSHIP)',  type: 'date' },
  { key: 'dc_date',                             label: 'DC Date',            type: 'date' },
  { key: 'lane_pickup_date_or_deliver_before',  label: 'Pickup/Deliver By', type: 'date' },
  { key: 'ship_to',                             label: 'Ship To' },
  { key: 'customer',                            label: 'Customer/Buyer' },
  { key: 'vendor_name_number',                  label: 'Vendor Name/Number' },
  { key: 'factory',                             label: 'Factory' },
  { key: 'coo',                                 label: 'COO' },
  { key: 'shipmode',                            label: 'Ship Mode' },
  { key: 'incoterm',                            label: 'Incoterm' },
  { key: 'incoterm_origin',                     label: 'Incoterm Origin' },
  { key: 'po_status',                           label: 'PO Status' },
  { key: 'season',                              label: 'Season' },
  { key: 'loft_ticket_type',                    label: 'Loft Ticket Type' },  // LOFT header field
  // ── Core line item fields ──
  { key: 'customer_style_number',               label: 'Style #' },
  { key: 'customer_style_name',                 label: 'Style Name' },
  { key: 'customer_style_description',          label: 'Style Desc.' },
  { key: 'row_number',                          label: 'Row #' },
  { key: 'pack_method_type',                    label: 'Pack Type' },
  { key: 'color',                               label: 'Color' },
  { key: 'secondary_color',                     label: 'Secondary Color' },
  { key: 'size',                                label: 'Size' },
  { key: 'upc',                                 label: 'UPC' },
  { key: 'pieces_to_ship',                      label: 'Pcs to Ship' },
  { key: 'unit_of_measure',                     label: 'UOM' },
  { key: 'pack_quantity',                       label: 'Pack Qty' },
  { key: 'qty_of_prepacks',                     label: 'Qty Prepacks' },
  { key: 'us_retail_price',                     label: 'US Retail' },
  { key: 'sell_price',                          label: 'Sell Price' },
  { key: 'total_row_dollar_value',              label: 'Row Total $' },
  { key: 'prepack_sku',                         label: 'Prepack SKU' },
  { key: 'prepack_article_number',              label: 'Prepack Article #' },
  { key: 'class_code',                          label: 'Class Code' },
  { key: 'hts_description',                     label: 'HTS Desc.' },
  // ── Vendor-specific line item fields ──
  { key: 'article_number',                      label: 'Article #' },
  { key: 'department',                          label: 'Department' },
  { key: 'ae_store_type_code',                  label: 'AE Store Type Code' },
  { key: 'vss_number',                          label: 'VSS #' },
  { key: 'lanebryant_size',                     label: 'LB Size' },
  { key: 'packaging',                           label: 'Packaging' },
  { key: 'lanebryant_vendor_style_number',      label: 'LB Vendor Style #' },
  { key: 'free_people_vendor_style_number',     label: 'FP Vendor Style #' },
];

// Fields available per vendor (header + line item), cross-verified against main_v2.py
// column_mapping_header and column_mapping_lines for each vendor path.
const VENDOR_FIELDS = {
  american_eagle: new Set([
    // header (from AE extractor)
    'customer_po_number', 'customer_distribution_channel', 'initial_order_date',
    'po_version_number', 'po_version_date', 'bshipdate', 'dc_date', 'ship_to',
    'customer', 'factory', 'shipmode', 'incoterm', 'incoterm_origin',
    // line items (from AE extractor)
    'customer_style_name', 'customer_style_number', 'article_number', 'department',
    'row_number', 'pack_method_type', 'color', 'secondary_color', 'size', 'upc',
    'pieces_to_ship', 'unit_of_measure', 'pack_quantity', 'qty_of_prepacks',
    'us_retail_price', 'sell_price', 'total_row_dollar_value', 'prepack_sku',
    'prepack_article_number', 'class_code', 'hts_description', 'ae_store_type_code',
    'customer_style_description',
  ]),
  chico: new Set([
    // header
    'customer_po_number', 'initial_order_date', 'bshipdate', 'dc_date', 'ship_to',
    'vendor_name_number', 'factory', 'shipmode', 'incoterm', 'po_status',
    // line items (generic path — all mapped fields can appear)
    'customer_style_name', 'customer_style_number', 'article_number',
    'customer_style_description', 'department', 'row_number', 'pack_method_type',
    'color', 'secondary_color', 'size', 'upc', 'pieces_to_ship', 'unit_of_measure',
    'pack_quantity', 'qty_of_prepacks', 'us_retail_price', 'sell_price',
    'total_row_dollar_value', 'prepack_sku', 'prepack_article_number',
    'class_code', 'hts_description',
  ]),
  freepeople: new Set([
    // header
    'customer_po_number', 'customer_distribution_channel', 'initial_order_date',
    'po_version_number', 'po_version_date', 'bshipdate', 'dc_date', 'ship_to',
    'vendor_name_number', 'factory', 'coo', 'shipmode', 'incoterm',
    // line items (generic path)
    'customer_style_name', 'customer_style_number', 'article_number',
    'customer_style_description', 'department', 'row_number', 'pack_method_type',
    'color', 'secondary_color', 'size', 'upc', 'pieces_to_ship', 'unit_of_measure',
    'pack_quantity', 'qty_of_prepacks', 'us_retail_price', 'sell_price',
    'total_row_dollar_value', 'prepack_sku', 'prepack_article_number',
    'class_code', 'hts_description', 'free_people_vendor_style_number',
  ]),
  lane_bryant: new Set([
    // header
    'customer_po_number', 'customer_distribution_channel', 'initial_order_date',
    'po_version_number', 'po_version_date', 'bshipdate', 'dc_date', 'ship_to',
    'vendor_name_number', 'factory', 'coo', 'shipmode', 'incoterm', 'incoterm_origin',
    'lane_pickup_date_or_deliver_before',
    // line items (generic path)
    'customer_style_name', 'customer_style_number', 'article_number',
    'customer_style_description', 'department', 'row_number', 'pack_method_type',
    'color', 'secondary_color', 'size', 'upc', 'pieces_to_ship', 'unit_of_measure',
    'pack_quantity', 'qty_of_prepacks', 'us_retail_price', 'sell_price',
    'total_row_dollar_value', 'prepack_sku', 'prepack_article_number',
    'class_code', 'hts_description', 'vss_number',
    'lanebryant_size', 'packaging', 'lanebryant_vendor_style_number',
  ]),
  mast: new Set([
    // header
    'customer_po_number', 'sub_po', 'customer_distribution_channel', 'initial_order_date',
    'po_version_number', 'po_version_date', 'bshipdate', 'dc_date', 'ship_to',
    'vendor_name_number', 'factory', 'coo', 'shipmode', 'incoterm', 'incoterm_origin',
    'po_status', 'season',
    // line items (generic path)
    'customer_style_name', 'customer_style_number', 'article_number',
    'customer_style_description', 'department', 'row_number', 'pack_method_type',
    'color', 'secondary_color', 'size', 'upc', 'pieces_to_ship', 'unit_of_measure',
    'pack_quantity', 'qty_of_prepacks', 'us_retail_price', 'sell_price',
    'total_row_dollar_value', 'prepack_sku', 'prepack_article_number',
    'class_code', 'hts_description', 'vss_number',
  ]),
  loft: new Set([
    // header (from generic column_mapping_header + loft_ticket_type)
    'customer_po_number', 'sub_po', 'customer_distribution_channel', 'initial_order_date',
    'po_version_number', 'po_version_date', 'bshipdate', 'dc_date', 'ship_to',
    'customer', 'vendor_name_number', 'factory', 'coo', 'shipmode', 'incoterm',
    'incoterm_origin', 'po_status', 'season', 'lane_pickup_date_or_deliver_before',
    'loft_ticket_type',                      // LOFT-specific header field
    // line items (Talbots/LOFT column_mapping_lines)
    'customer_style_name', 'customer_style_number', 'article_number',
    'customer_style_description', 'department', 'row_number', 'pack_method_type',
    'color', 'secondary_color', 'size', 'upc', 'pieces_to_ship', 'unit_of_measure',
    'pack_quantity', 'qty_of_prepacks', 'us_retail_price', 'sell_price',
    'total_row_dollar_value', 'prepack_sku', 'prepack_article_number',
    'class_code', 'hts_description', 'vss_number',
  ]),
  talbots: new Set([
    // header (Talbots column_mapping_header)
    'customer_po_number', 'sub_po', 'customer_distribution_channel', 'initial_order_date',
    'po_version_number', 'po_version_date', 'bshipdate', 'dc_date', 'ship_to',
    'customer', 'vendor_name_number', 'factory', 'coo', 'shipmode', 'incoterm',
    'incoterm_origin', 'po_status', 'season', 'lane_pickup_date_or_deliver_before',
    // line items (Talbots column_mapping_lines)
    'customer_style_name', 'customer_style_number', 'article_number',
    'customer_style_description', 'department', 'row_number', 'pack_method_type',
    'color', 'secondary_color', 'size', 'upc', 'pieces_to_ship', 'unit_of_measure',
    'pack_quantity', 'qty_of_prepacks', 'us_retail_price', 'sell_price',
    'total_row_dollar_value', 'prepack_sku', 'prepack_article_number',
    'class_code', 'hts_description', 'vss_number',
  ]),
};



const FIXED_RULES = [
  { id: 'rule_style', type: 'match_all', field: 'customer_style_number', enabled: true, name: 'Same Style #' },
  { id: 'rule_size',  type: 'match_all', field: 'size',                  enabled: true, name: 'Same Size'    },
  { id: 'rule_color', type: 'match_all', field: 'color',                 enabled: true, name: 'Same Color'   },
];

const LineItemFilterPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [vendor, setVendor] = useState(() => sessionStorage.getItem('li_vendor') || 'american_eagle');
  // Fields available for rule creation, filtered to only those relevant to the selected vendor
  const vendorFields = useMemo(() => {
    const allowed = VENDOR_FIELDS[vendor];
    if (!allowed) return FIELDS;
    return FIELDS.filter(f => allowed.has(f.key));
  }, [vendor]);
  const [dateField, setDateField] = useState(() => sessionStorage.getItem('li_dateField') || 'initial_order_date');
  const [fromDate, setFromDate] = useState(() => {
    try { const s = sessionStorage.getItem('li_fromDate'); return s ? new Date(s) : null; } catch { return null; }
  });
  const [toDate, setToDate] = useState(() => {
    try { const s = sessionStorage.getItem('li_toDate'); return s ? new Date(s) : null; } catch { return null; }
  });
  const [lineItems, setLineItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filterColumns, setFilterColumns] = useState(() => {
    try { const saved = localStorage.getItem('lineItemFilterColumns'); return saved ? JSON.parse(saved) : []; } catch { return []; }
  });
  const [filterMode, setFilterMode] = useState(() => localStorage.getItem('lineItemFilterMode') || 'same');
  const [filterValues, setFilterValues] = useState(() => {
    try { const saved = localStorage.getItem('lineItemFilterValues'); return saved ? JSON.parse(saved) : {}; } catch { return {}; }
  });
  
  const DEFAULT_COLUMNS = ['customer_po_number', 'customer_style_number', 'color', 'size', 'pieces_to_ship', 'pack_method_type', 'sell_price'];
  const [selectedColumns, setSelectedColumns] = useState(() => {
    try { 
      const saved = localStorage.getItem('lineItemCols'); 
      const parsed = saved ? JSON.parse(saved) : DEFAULT_COLUMNS; 
      if (!parsed.includes('customer_po_number')) parsed.unshift('customer_po_number');
      return parsed;
    } catch { return DEFAULT_COLUMNS; }
  });
  const [colMenuAnchor, setColMenuAnchor] = useState(null);
  const [dragColKey, setDragColKey] = useState(null);
  const [dragOverColKey, setDragOverColKey] = useState(null);
  
  // Multiple saved filters state
  const [savedFilterPresets, setSavedFilterPresets] = useState(() => {
    try { const saved = localStorage.getItem('lineItemSavedFilters'); return saved ? JSON.parse(saved) : []; } catch { return []; }
  });
  const [saveFilterDialogOpen, setSaveFilterDialogOpen] = useState(false);
  const [filterPresetName, setFilterPresetName] = useState('');
  const [savedFiltersAnchor, setSavedFiltersAnchor] = useState(null);
  const [activeFilterPresetId, setActiveFilterPresetId] = useState(null);
  const [uniqueValues, setUniqueValues] = useState({});
  const [lastFetchedAt, setLastFetchedAt] = useState(null);

  // Grouping state — Create new group
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [groupViolations, setGroupViolations] = useState([]);
  const [showGroupViolations, setShowGroupViolations] = useState(false);
  const [grouping, setGrouping] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, msg: '', severity: 'success' });
  const [customRules, setCustomRules] = useState([]);
  const [enabledRuleIds, setEnabledRuleIds] = useState(new Set(FIXED_RULES.map(r => r.id)));
  const [addRuleOpen, setAddRuleOpen] = useState(false);
  const [newRuleField, setNewRuleField] = useState('');
  // ── Multi-rule-sets (new system) ──────────────────────────────────────
  // vendorRef lets us read the current vendor synchronously inside callbacks
  // without adding it as an effect dependency.
  const vendorRef = React.useRef('american_eagle');

  // Fast initial display from localStorage cache while the DB fetch is in-flight.
  const [ruleSets, setRuleSets] = useState(() => {
    const initialVendor = 'american_eagle';
    try {
      const cached = localStorage.getItem(`po_rule_sets_${initialVendor}`);
      if (cached) return JSON.parse(cached);
    } catch {}
    const defaults = VENDOR_DEFAULT_RULE_SETS[initialVendor] || [];
    return defaults.map(rs => ({ ...rs, rules: rs.rules.map(r => ({ ...r })) }));
  });
  const [rulesLoading, setRulesLoading] = useState(false);
  const [ruleConfigOpen, setRuleConfigOpen] = useState(false);
  const [showGrouped, setShowGrouped] = useState(false);
  const [showExcluded, setShowExcluded] = useState(false);
  const tableContainerRef = React.useRef(null);
  const topScrollRef      = React.useRef(null);
  const autoScrollRef     = React.useRef(null);
  const [tickedIds, setTickedIds] = useState(new Set());
  const [crossedIds, setCrossedIds] = useState(new Set());
  const [applyingGroups, setApplyingGroups] = useState(false);
  const [batchQueue, setBatchQueue] = useState([]);   // [{gid, name, items:[]}]
  const [batchIndex, setBatchIndex] = useState(0);
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchConfirming, setBatchConfirming] = useState(false);
  const [confirmedGids, setConfirmedGids] = useState(new Set()); // GIDs confirmed in current batch session
  const [batchExcludedIds, setBatchExcludedIds] = useState(new Set()); // item IDs unchecked by user in current dialog
  const [batchGroupName, setBatchGroupName] = useState('');
  const [batchBernadoStyle, setBatchBernadoStyle] = useState('');
  const [debouncedImgStyle, setDebouncedImgStyle] = useState('');
  const [batchCustomerItem, setBatchCustomerItem] = useState(null); // { customer_item, is_new } — auto-fetched from i2
  const [batchCustomerItemOverride, setBatchCustomerItemOverride] = useState(''); // manual user entry
  const [batchCustomerItemLoading, setBatchCustomerItemLoading] = useState(false);
  const [batchImgLoading, setBatchImgLoading] = useState(false);
  const [batchImgNotFound, setBatchImgNotFound] = useState(false);
  const [batchImgModalOpen, setBatchImgModalOpen] = useState(false);
  const [batchStyleIsSuggested, setBatchStyleIsSuggested] = useState(false);

  // Configurable columns for the items table inside the batch group dialog.
  // Persisted to localStorage so the user's choice survives page reloads.
  const BATCH_DIALOG_ALL_COLUMNS = [
    { key: 'customer_po_number',            label: 'PO #' },
    { key: 'customer_style_number',         label: 'Style #' },
    { key: 'color',                         label: 'Color' },
    { key: 'size',                          label: 'Size' },
    { key: 'pack_method_type',              label: 'Pack Type' },
    { key: 'ship_to',                       label: 'Ship To' },
    { key: 'bshipdate',                     label: 'Ship Date (BShip)' },
    { key: 'initial_order_date',            label: 'Order Date' },
    { key: 'dc_date',                       label: 'DC Date' },
    { key: 'customer_style_name',           label: 'Style Name' },
    { key: 'customer_style_description',    label: 'Style Desc.' },
    { key: 'quantity',                      label: 'Qty' },
    { key: 'sub_po',                        label: 'Sub PO' },
    { key: 'factory',                       label: 'Factory' },
    { key: 'coo',                           label: 'COO' },
    { key: 'season',                        label: 'Season' },
    { key: 'shipmode',                      label: 'Ship Mode' },
    { key: 'incoterm',                      label: 'Incoterm' },
    { key: 'customer_distribution_channel', label: 'Distribution Channel' },
    { key: 'vendor_name_number',            label: 'Vendor Name/Number' },
    { key: 'ticket_description',            label: 'Ticket Description' },
  ];
  const BATCH_DIALOG_DEFAULT_COLS = ['customer_po_number', 'customer_style_number', 'color', 'size', 'pack_method_type', 'ship_to', 'bshipdate'];
  const [batchDialogColumns, setBatchDialogColumns] = useState(() => {
    try {
      const saved = localStorage.getItem('batch_dialog_columns');
      if (saved) return JSON.parse(saved);
    } catch {}
    return BATCH_DIALOG_DEFAULT_COLS;
  });
  const [colPickerAnchor, setColPickerAnchor] = useState(null);
  const batchDragColRef = React.useRef(null); // key of the column being dragged
  const [bernadoStyleForGroup, setBernadoStyleForGroup] = useState('');
  const [fetchedGroups, setFetchedGroups] = useState([]);
  const [groupConfirmDialog, setGroupConfirmDialog] = useState(null);
  // { group: {...}, item: {...}, mode: 'grouped' | 'existing_suggestion' }
  // Shown when a new item's value_in field value isn't yet in the existing suggested group —
  // lets the user choose between adding to the existing group or creating a new one.
  const [valueInChoiceDialog, setValueInChoiceDialog] = useState(null); // { item, group }
  // Shown when multiple existing groups share the same rule key — user must pick which group
  // to add the item to, or create a new one.
  const [ambiguousGroupDialog, setAmbiguousGroupDialog] = useState(null); // { item, matchingGroups }
  // Which rule set cards are expanded (showing chips). Collapsed by default.
  const [expandedRuleSets, setExpandedRuleSets] = useState(new Set());
  const toggleRuleSetExpanded = (id) => setExpandedRuleSets(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  // Inline editor for value_in rule — lets user add/remove permitted values without opening full RuleConfigDialog
  const [valueInEditDialog, setValueInEditDialog] = useState(null); // { rsId, rule }
  const [valueInEditInput, setValueInEditInput] = useState('');
  const [valueInEditValues, setValueInEditValues] = useState([]);
  const [dialogImgStyle, setDialogImgStyle] = useState('');
  const [dialogImgSearched, setDialogImgSearched] = useState('');
  const [dialogImgModal, setDialogImgModal] = useState(false);

  const allRules = [...FIXED_RULES, ...customRules];
  const currentRules = allRules.filter(r => enabledRuleIds.has(r.id));

  // Load rule sets when vendor changes.
  // Strategy:
  //  1. Show localStorage cache instantly (no flicker / loading delay).
  //  2. Fetch from DB — DB is authoritative.
  //     • found=true  → user has explicitly saved rules for this vendor; use DB data.
  //     • found=false → vendor has never saved rules; fall back to code-level defaults.
  //  3. If DB fetch fails, keep whatever localStorage showed (graceful degradation).
  React.useEffect(() => {
    vendorRef.current = vendor;

    // Sub-vendors share rules with their parent vendor
    const ruleVendor = RULE_VENDOR_ALIAS[vendor] || vendor;

    // Step 1 — instant display from cache
    try {
      const cached = localStorage.getItem(`po_rule_sets_${ruleVendor}`);
      if (cached) {
        setRuleSets(JSON.parse(cached));
      } else {
        const defaults = VENDOR_DEFAULT_RULE_SETS[ruleVendor] || [];
        setRuleSets(defaults.map(rs => ({ ...rs, rules: rs.rules.map(r => ({ ...r })) })));
      }
    } catch {}

    // Step 2 — fetch from DB (source of truth)
    setRulesLoading(true);
    axios.get(`${API}/api/rule-sets/${ruleVendor}`)
      .then(res => {
        const { rule_sets: dbRules, found } = res.data;
        if (found) {
          // DB is authoritative — sync localStorage and state together to keep them in step.
          setRuleSets(dbRules);
          localStorage.setItem(`po_rule_sets_${ruleVendor}`, JSON.stringify(dbRules));
        }
        // found=false → vendor rules were never explicitly saved to DB.
        // Keep whatever Step 1 already loaded (localStorage cache or code-level defaults).
        // Do NOT reset to defaults here — that would cause the "old rules flash then
        // disappear" bug when localStorage has valid rules but the DB row is missing.
      })
      .catch(() => {
        // DB unreachable — keep the localStorage data already shown in step 1
      })
      .finally(() => setRulesLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendor]);

  // Derived: show items matching grouped-status toggle; hide rule-excluded items unless showExcluded is on
  const displayItems = filteredItems.filter(item => {
    if (showGrouped) return item._is_grouped === true;
    if (item._is_grouped) return false;
    if (item._excluded_by_rules && !showExcluded) return false;
    return true;
  });
  const excludedCount = filteredItems.filter(i => !i._is_grouped && i._excluded_by_rules).length;

  const toggleRule = (id) => {
    setEnabledRuleIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Warn on in-app navigation away when state is non-default
  const hasUnsavedState =
    lineItems.length > 0 ||
    customRules.length > 0 ||
    enabledRuleIds.size !== FIXED_RULES.length;

  const blocker = useBlocker(hasUnsavedState);

  // Warn on browser refresh / tab close
  useEffect(() => {
    const handler = (e) => {
      if (hasUnsavedState) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedState]);
  const [viewedGroup, setViewedGroup] = useState(null);
  const [suggestedItem, setSuggestedItem] = useState(null);
  const [mergingGroupId, setMergingGroupId] = useState(null);

  // Add-to-Group state
  const [atgOpen, setAtgOpen] = useState(false);
  const [existingGroups, setExistingGroups] = useState([]);
  const [atgSelectedGroup, setAtgSelectedGroup] = useState(null);
  const [atgViolations, setAtgViolations] = useState([]);
  const [showAtgViolations, setShowAtgViolations] = useState(false);
  const [atgSaving, setAtgSaving] = useState(false);
  const [atgLoading, setAtgLoading] = useState(false);

  // Only show columns that have at least 1 non-empty value in the fetched data
  const [visibleFields, setVisibleFields] = useState(FIELDS);

  // On unmount: clear heavy cached data (large arrays) but KEEP the filter inputs
  // (vendor, dateField, fromDate, toDate) so the user's dates survive navigating away
  // and coming back within the same browser session.
  useEffect(() => {
    return () => {
      sessionStorage.removeItem('li_items');
      sessionStorage.removeItem('li_filtered');
      sessionStorage.removeItem('li_visibleFields');
    };
  }, []);

  useEffect(() => {
    const u = {};
    filterColumns.forEach(col => {
      const vals = new Set();
      lineItems.forEach(item => {
        if (item[col] != null && String(item[col]).trim()) vals.add(String(item[col]));
      });
      u[col] = Array.from(vals).sort();
    });
    setUniqueValues(u);
  }, [filterColumns, lineItems]);

  useEffect(() => { localStorage.setItem('lineItemCols', JSON.stringify(selectedColumns)); }, [selectedColumns]);
  useEffect(() => { localStorage.setItem('lineItemSavedFilters', JSON.stringify(savedFilterPresets)); }, [savedFilterPresets]);

  // Persist filter inputs to sessionStorage so they survive same-session navigation
  // (e.g. user goes to View Groups and comes back — dates are still set)
  useEffect(() => { sessionStorage.setItem('li_vendor', vendor); }, [vendor]);
  useEffect(() => { sessionStorage.setItem('li_dateField', dateField); }, [dateField]);

  // Auto-set from date to earliest date for the current vendor+dateField, then auto-fetch
  const autoInitRef = useRef(false);
  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      try {
        const res = await axios.get(`${API}/api/line-items/date-range`, {
          params: { vendor, date_field: dateField },
        });
        if (cancelled) return;
        if (res.data?.earliest) {
          setFromDate(new Date(res.data.earliest));
        }
        autoInitRef.current = true;
        pendingAutoFetchRef.current = true;
      } catch {
        // ignore — user can set date manually
      }
    };
    // Clear stale data immediately on vendor change
    setLineItems([]);
    setFilteredItems([]);
    setFromDate(null);
    setToDate(null);
    autoInitRef.current = false;
    pendingAutoFetchRef.current = false;
    init();
    return () => { cancelled = true; };
  }, [vendor, dateField]); // eslint-disable-line react-hooks/exhaustive-deps

  const pendingAutoFetchRef = useRef(false);
  useEffect(() => {
    if (fromDate) sessionStorage.setItem('li_fromDate', fromDate.toISOString());
    else sessionStorage.removeItem('li_fromDate');
  }, [fromDate]);
  useEffect(() => {
    if (toDate) sessionStorage.setItem('li_toDate', toDate.toISOString());
    else sessionStorage.removeItem('li_toDate');
  }, [toDate]);

  // Show the loading spinner immediately whenever the style number changes (covers dialog open,
  // advance, navigation, typing, and paste — all in one place so nothing is missed).
  useEffect(() => {
    if (batchBernadoStyle.trim()) {
      setBatchImgLoading(true);
      setBatchImgNotFound(false);   // reset "not found" on every new value
    } else {
      setBatchImgLoading(false);
      setDebouncedImgStyle('');
    }
  }, [batchBernadoStyle]);

  // Keep the top scrollbar spacer width = actual table scroll width so the mirror scrollbar
  // has the correct range. Runs whenever columns change or data loads.
  useEffect(() => {
    const sync = () => {
      const container = tableContainerRef.current;
      const spacer    = document.getElementById('top-scroll-spacer');
      if (container && spacer) spacer.style.width = container.scrollWidth + 'px';
    };
    sync();
    const observer = new ResizeObserver(sync);
    if (tableContainerRef.current) observer.observe(tableContainerRef.current);
    return () => observer.disconnect();
  }, [selectedColumns, filteredItems]);

  // Dynamic column widths: compute the ideal min-width (px) for each column
  // based on the widest value in that column (across all filtered items + header label).
  const colWidths = useMemo(() => {
    const CHAR_PX = 8.5;   // approximate px per character at 0.85rem font
    const MIN_W   = 80;
    const MAX_W   = 320;
    const PAD     = 36;    // left + right cell padding
    const result  = {};
    for (const key of selectedColumns) {
      const field = visibleFields.find(f => f.key === key);
      if (!field) continue;
      if (key === '_suggested_group_id')   { result[key] = 100; continue; }
      if (key === '_suggested_group_name') { result[key] = 240; continue; }
      let maxLen = (field.label || key).length;
      for (const item of filteredItems) {
        const val = item[key];
        if (val != null) {
          const len = String(val).length;
          if (len > maxLen) maxLen = len;
        }
      }
      result[key] = Math.min(MAX_W, Math.max(MIN_W, Math.round(maxLen * CHAR_PX) + PAD));
    }
    return result;
  }, [selectedColumns, filteredItems, visibleFields]);

  // Debounce the img src — wait 500 ms of no typing before firing the actual HTTP request,
  // so rapid keystrokes don't flood the external image API.
  useEffect(() => {
    const trimmed = batchBernadoStyle.trim();
    if (!trimmed) return;
    const timer = setTimeout(() => setDebouncedImgStyle(trimmed), 500);
    return () => clearTimeout(timer);
  }, [batchBernadoStyle]);

  // Debounced customer item preview — fetch from i2 when Bernardo Style # is set
  useEffect(() => {
    const trimmed = batchBernadoStyle.trim();
    if (!trimmed) { setBatchCustomerItem(null); setBatchCustomerItemOverride(''); return; }
    // Use the actual vendor stored on the line items (e.g. american_eagle_mens),
    // not the dropdown vendor which is just a filter and may be the parent (american_eagle).
    const itemVendor = batchQueue[batchIndex]?.items?.[0]?.vendor || vendor;
    const timer = setTimeout(async () => {
      try {
        setBatchCustomerItemLoading(true);
        const res = await axios.get(`${API}/api/i2/customer-item-preview`, {
          params: { bernardo_style: trimmed, vendor: itemVendor },
        });
        setBatchCustomerItem(res.data);
      } catch {
        setBatchCustomerItem(null);
      } finally {
        setBatchCustomerItemLoading(false);
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [batchBernadoStyle, batchIndex, batchQueue, vendor]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync Customer Item # from i2 preview. Always replace the field — including when
  // i2 returns null (manual-entry vendors / new styles) — so a manual edit from the
  // previous batch group never sticks into the next one.
  //   • AllowUserItemNumber=0 (e.g. Chico's) — auto-generated / existing number
  //   • AllowUserItemNumber=1 + existing in i2 (reorder) — suggest the known value
  //   • AllowUserItemNumber=1 + no match — clear field so the user types fresh
  useEffect(() => {
    if (!batchCustomerItem) return;
    setBatchCustomerItemOverride(batchCustomerItem.customer_item || '');
  }, [batchCustomerItem]);

  // Auto-fetch image in Group Confirm Dialog — same debounce pattern.
  // User pastes/types the Bernardo style# and the image loads automatically.
  useEffect(() => {
    const trimmed = dialogImgStyle.trim();
    if (!trimmed) { setDialogImgSearched(''); return; }
    const timer = setTimeout(() => setDialogImgSearched(trimmed), 500);
    return () => clearTimeout(timer);
  }, [dialogImgStyle]);

  const handleApplyFilter = () => {
    if (!filterColumns.length) { setFilteredItems(lineItems.map(i => ({ ...i, _isMatch: false }))); return; }
    const matches = [], nonMatches = [];
    lineItems.forEach(item => {
      let isMatch = true, anyFilter = false;
      for (const col of filterColumns) {
        const v = String(item[col] || '');
        if (filterMode === 'same') {
          const sel = filterValues[col] || [];
          if (sel.length) { anyFilter = true; if (!sel.includes(v)) { isMatch = false; break; } }
        } else {
          const s = filterValues[col] || '';
          if (s.trim()) { anyFilter = true; if (!v.toLowerCase().includes(s.toLowerCase())) { isMatch = false; break; } }
        }
      }
      if (anyFilter && isMatch) matches.push({ ...item, _isMatch: true });
      else nonMatches.push({ ...item, _isMatch: false });
    });

    // Sort matches so that identical filtered values appear together sequentially
    matches.sort((a, b) => {
      for (const col of filterColumns) {
        const valA = String(a[col] || '').toLowerCase();
        const valB = String(b[col] || '').toLowerCase();
        if (valA < valB) return -1;
        if (valA > valB) return 1;
      }
      return 0;
    });

    setFilteredItems([...matches, ...nonMatches]);
  };

  const handleSaveFilterPreset = () => {
    if (!filterPresetName.trim()) return;
    const newPreset = {
      id: Date.now().toString(),
      name: filterPresetName.trim(),
      filterColumns,
      filterMode,
      filterValues
    };
    setSavedFilterPresets([...savedFilterPresets, newPreset]);
    setSaveFilterDialogOpen(false);
    setFilterPresetName('');
    setSnackbar({ open: true, msg: 'Filter preset saved successfully', severity: 'success' });
  };

  const handleLoadFilterPreset = (preset) => {
    setFilterColumns(preset.filterColumns);
    setFilterMode(preset.filterMode);
    setFilterValues(preset.filterValues);
    setActiveFilterPresetId(preset.id);
    setSavedFiltersAnchor(null);
    setSnackbar({ open: true, msg: `Loaded filter: ${preset.name}`, severity: 'info' });
  };

  const handleDeleteFilterPreset = (id, e) => {
    e.stopPropagation();
    setSavedFilterPresets(savedFilterPresets.filter(p => p.id !== id));
    if (activeFilterPresetId === id) setActiveFilterPresetId(null);
  };

  const handleSaveFilters = () => {
    localStorage.setItem('lineItemFilterColumns', JSON.stringify(filterColumns));
    localStorage.setItem('lineItemFilterMode', filterMode);
    localStorage.setItem('lineItemFilterValues', JSON.stringify(filterValues));
    setSnackbar({ open: true, msg: 'Current configuration saved as default', severity: 'success' });
  };

  const handleClearFilters = () => {
    setFilterColumns([]);
    setFilterValues({});
    setFilterMode('same');
    setActiveFilterPresetId(null);
    localStorage.removeItem('lineItemFilterColumns');
    localStorage.removeItem('lineItemFilterMode');
    localStorage.removeItem('lineItemFilterValues');
    setSnackbar({ open: true, msg: 'Filters cleared', severity: 'info' });
  };

  const handleUpdateFilterPreset = () => {
    if (!activeFilterPresetId) return;
    const updatedPresets = savedFilterPresets.map(p => {
      if (p.id === activeFilterPresetId) {
        return { ...p, filterColumns, filterMode, filterValues };
      }
      return p;
    });
    setSavedFilterPresets(updatedPresets);
    setSnackbar({ open: true, msg: 'Filter preset updated successfully', severity: 'success' });
  };


  const handleFetchData = async () => {
    try {
      setLoading(true); setError('');
      setSelectedIds(new Set());
      setTickedIds(new Set());
      setCrossedIds(new Set());

      // Snapshot active rules RIGHT NOW before any async/re-render can change them
      const activeRules = allRules.filter(r => enabledRuleIds.has(r.id));
      // Snapshot active rule sets for multi-set grouping
      const activeRuleSets = ruleSets.filter(rs => rs.enabled);

      // makeKey with multi-rule-set + value_in support
      const makeKeyForRuleSet = (item, ruleSet) => {
        const parts = [`__RS:${ruleSet.id}`];
        for (const rule of ruleSet.rules) {
          if (!rule.enabled) continue;
          if (rule.type === 'match_all') {
            parts.push(norm(item[rule.field]));
          } else if (rule.type === 'value_in') {
            const itemVal = norm(item[rule.field]);
            const allowed = (rule.values || []).map(v => norm(v));
            if (allowed.length > 0) {
              const matches = allowed.some(a => a === itemVal || itemVal.includes(a) || a.includes(itemVal));
              if (!matches) return null; // item excluded from this rule set
            }
            // matched: do NOT add to key — different matching values can coexist in same group
          } else if (rule.type === 'isolate') {
            parts.push(norm(item[rule.field])); // isolate by including value in key
          }
          // max_days / pack_bulk_web: validation only, not part of grouping key
        }
        return parts.join('||');
      };

      // Use local date components instead of UTC (toISOString) to avoid timezone
      // offset shifting the date — e.g. in UTC+5, midnight local = prev day in UTC.
      const toLocalDateStr = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
      };
      const params = new URLSearchParams({ vendor, date_field: dateField });
      if (fromDate) params.append('start_date', toLocalDateStr(fromDate));
      if (toDate) params.append('end_date', toLocalDateStr(toDate));

      const [res, groupsRes] = await Promise.all([
        axios.get(`${API}/api/line-items/filter?${params}`),
        axios.get(`${API}/api/line-item-groups?vendor=${vendor}`)
      ]);

      let data = res.data?.data || [];
      const groups = groupsRes.data?.data || [];
      setFetchedGroups(groups);

      // Map line_item_id → group
      const itemGroupMap = {};
      groups.forEach(g => {
        (g.line_item_ids || []).forEach(id => { itemGroupMap[id] = g; });
      });

      data = data.map(item => {
        const isGrouped = !!itemGroupMap[item.id];
        return {
          ...item,
          _is_grouped: isGrouped,
          _group_name: itemGroupMap[item.id]?.group_name || '',
          _group_object: itemGroupMap[item.id] || null,
        };
      });

      // ── Suggested group computation ──────────────────────────────────────
      // Use activeRules (snapshot) — never the live closure value
      // Normalise a field value for grouping key comparison.
      // Handles invisible Unicode variants (non-breaking spaces, en-dashes,
      // zero-width chars, BOM) that look the same visually but differ as bytes.
      const norm = (val) => {
        return String(val ?? '')
          .normalize('NFC')
          .replace(/\u00a0/g, ' ')
          .replace(/[\u200b\u200c\u200d\ufeff]/g, '')
          .replace(/[\u2000-\u200a\u202f\u205f\u3000]/g, ' ')
          .replace(/\s+/g, ' ')
          .trim()
          .toLowerCase();
      };
      const makeKey = (item) => {
        // Try each active rule set — return the first matching key
        for (const rs of activeRuleSets) {
          const k = makeKeyForRuleSet(item, rs);
          if (k !== null) return k;
        }
        // If rule sets are configured and the item didn't match any of them
        // (e.g. its ship_to is not in the value_in permitted list), return null
        // so it gets NO suggested group rather than falling through to legacy rules.
        if (activeRuleSets.length > 0) return null;
        // Fallback: no rule sets configured — use legacy chip-based rules
        return activeRules.map(r => norm(item[r.field])).join('||');
      };

      // Always produces a non-empty name for an item, regardless of which fields are populated.
      const itemFallbackName = (item) =>
        [item.customer_style_number, item.color, item.size, item.customer_po_number]
          .map(v => String(v ?? '').trim()).filter(Boolean).join('_') || `Group_${item.id}`;

      const makeNameForItem = (item) => {
        const buildName = (fields) =>
          fields
            .map(r => String(item[r.field] ?? '').trim())
            .filter(v => v !== '')
            .join('_');

        for (const rs of activeRuleSets) {
          const k = makeKeyForRuleSet(item, rs);
          if (k !== null) {
            const name = buildName(rs.rules.filter(r => r.enabled && r.type === 'match_all'));
            return name || itemFallbackName(item);
          }
        }
        if (activeRuleSets.length > 0) return itemFallbackName(item);
        // fallback: no rule sets — legacy chip-based rules
        return buildName(activeRules.filter(r => r.field)) || itemFallbackName(item);
      };

      // Map normalised key → existing { id, name } from already-saved groups.
      // Built from GROUPED ITEMS in the current fetch (same API response, same raw
      // values) so keys are guaranteed to match ungrouped items from the same fetch.
      // Falling back to group_data[0] only when no grouped item appears in this fetch.
      const existingKeyMap = {};       // key → { id, name } — lowest-ID group (primary)
      const existingKeyAllGroups = {}; // key → [{ id, name, group_data }] — ALL groups for that key

      const _addToKeyAllGroups = (key, g) => {
        if (!existingKeyAllGroups[key]) existingKeyAllGroups[key] = [];
        if (!existingKeyAllGroups[key].some(x => String(x.id) === String(g.id)))
          existingKeyAllGroups[key].push({ id: g.id, name: g.group_name, group_data: g.group_data || [] });
      };

      // Pass A – use actual fetched items that belong to a group (most reliable)
      data.forEach(item => {
        const group = itemGroupMap[item.id];
        if (!group) return;
        const key = makeKey(item);
        if (key === null) return; // item doesn't qualify for any rule set
        _addToKeyAllGroups(key, group);
        const gId = Number(group.id);
        if (!existingKeyMap[key] || Number(existingKeyMap[key].id) > gId) {
          existingKeyMap[key] = { id: group.id, name: group.group_name };
        }
      });

      // Pass B – fallback: groups whose items aren't in this date range
      [...groups].sort((a, b) => Number(a.id) - Number(b.id)).forEach(g => {
        if (Array.isArray(g.group_data) && g.group_data.length > 0) {
          const key = makeKey(g.group_data[0]);
          if (key === null) return; // item doesn't qualify for any rule set
          _addToKeyAllGroups(key, g);
          if (!existingKeyMap[key]) existingKeyMap[key] = { id: g.id, name: g.group_name };
        }
      });

      // Build per-key sets of value_in field values already present in each existing group.
      // Used to detect items whose value_in field value is NEW (not yet in the group) so we
      // can show a choice: "add to existing group" vs "create new group".
      const activeValueInFields = [];
      for (const rs of activeRuleSets) {
        for (const rule of rs.rules) {
          if (rule.enabled && rule.type === 'value_in' && rule.field && !activeValueInFields.includes(rule.field))
            activeValueInFields.push(rule.field);
        }
      }
      const existingGroupValueSets = {}; // key → { field: Set<normValue> }
      if (activeValueInFields.length > 0) {
        [...groups].sort((a, b) => Number(a.id) - Number(b.id)).forEach(g => {
          if (!Array.isArray(g.group_data) || g.group_data.length === 0) return;
          const key = makeKey(g.group_data[0]);
          if (!key || !existingKeyMap[key] || String(existingKeyMap[key].id) !== String(g.id)) return;
          existingGroupValueSets[key] = {};
          activeValueInFields.forEach(field => {
            existingGroupValueSets[key][field] = new Set(
              g.group_data.map(di => norm(String(di[field] ?? ''))).filter(Boolean)
            );
          });
        });
      }

      // ── max_days helpers ────────────────────────────────────────────────────
      // Returns the max_days rule for whichever active rule set an item matches.
      const getItemMaxDaysRule = (item) => {
        for (const rs of activeRuleSets) {
          const k = makeKeyForRuleSet(item, rs);
          if (k !== null) {
            return rs.rules.find(r => r.enabled !== false && r.type === 'max_days') || null;
          }
        }
        return null;
      };

      // Pre-compute the date range already committed in each existing group.
      // Used in Pass 2 to reject items whose date would push the group over the limit.
      const groupDateRanges = {}; // groupId → { field, max, minTime, maxTime }
      groups.forEach(g => {
        const firstItem = (g.group_data || [])[0];
        if (!firstItem) return;
        const mdRule = getItemMaxDaysRule(firstItem);
        if (!mdRule) return;
        const dates = (g.group_data || [])
          .map(i => new Date(i[mdRule.field]))
          .filter(d => !isNaN(d.getTime()))
          .map(d => d.getTime());
        if (!dates.length) return;
        groupDateRanges[g.id] = {
          field: mdRule.field, max: mdRule.max,
          minTime: Math.min(...dates), maxTime: Math.max(...dates),
        };
      });

      // Returns true when adding itemDate into an existing group would still satisfy max_days.
      const isDateCompatibleWithGroup = (item, groupId) => {
        const range = groupDateRanges[groupId];
        if (!range) return true; // no max_days rule for this group
        const d = new Date(item[range.field]);
        if (isNaN(d.getTime())) return true; // item has no date — allow
        const newMin = Math.min(range.minTime, d.getTime());
        const newMax = Math.max(range.maxTime, d.getTime());
        const spreadDays = (newMax - newMin) / (1000 * 60 * 60 * 24);
        return spreadDays <= range.max;
      };
      // ────────────────────────────────────────────────────────────────────────

      // Pass 1 – one full scan to assign every unique key a stable NEW-N id.
      // For max_days keys: items are further split into date windows so no window
      // spans more than max days. Each window gets its own NEW-N id.
      // Counter starts after the highest existing DB group ID so the display
      // shows consecutive numbers (e.g. if max DB ID is 20, new groups show 21, 22…).
      const maxExistingId = groups.length > 0
        ? Math.max(0, ...groups.map(g => Number(g.id)).filter(n => !isNaN(n)))
        : 0;
      let tempCounter = maxExistingId + 1;
      const tempIdMap   = {};
      const tempNameMap = {};

      // Date-window registry: baseKey → [{ id, minTime, maxTime }]
      // Populated only for keys that have a max_days rule.
      const keyDateWindows = {};
      // Per-item window id: item.id → NEW-N (for items that needed date windowing)
      const itemWindowId = {};

      data.forEach(item => {
        if (item._is_grouped) return;
        const key = makeKey(item);
        if (key === null) return;

        // Items that already have a matching existing group are handled in Pass 2.
        // UNLESS that group would fail the date check — handle that below too.
        const existingId = existingKeyMap[key]?.id;
        const existingDateOk = !existingId || isDateCompatibleWithGroup(item, existingId);
        if (existingId && existingDateOk) return; // will be assigned to existing group in Pass 2

        // No compatible existing group — need a NEW-N id.
        const mdRule = getItemMaxDaysRule(item);

        if (!mdRule) {
          // No date constraint — one window per key
          if (!tempIdMap[key]) {
            tempIdMap[key]   = `NEW-${tempCounter++}`;
            tempNameMap[key] = makeNameForItem(item);
          }
        } else {
          // Date constraint — greedy date windowing
          const field    = mdRule.field;
          const maxDays  = mdRule.max;
          const itemDate = new Date(item[field]);
          const itemTime = isNaN(itemDate.getTime()) ? null : itemDate.getTime();

          if (!keyDateWindows[key]) keyDateWindows[key] = [];

          let assignedId = null;
          for (const win of keyDateWindows[key]) {
            if (itemTime === null) { assignedId = win.id; break; } // no date → first window
            const newMin = Math.min(win.minTime, itemTime);
            const newMax = Math.max(win.maxTime, itemTime);
            if ((newMax - newMin) / (1000 * 60 * 60 * 24) <= maxDays) {
              win.minTime = newMin;
              win.maxTime = newMax;
              assignedId  = win.id;
              break;
            }
          }

          if (!assignedId) {
            assignedId = `NEW-${tempCounter++}`;
            keyDateWindows[key].push({ id: assignedId, minTime: itemTime ?? 0, maxTime: itemTime ?? 0 });
            if (!tempNameMap[assignedId]) tempNameMap[assignedId] = makeNameForItem(item);
          }

          itemWindowId[item.id] = assignedId;
        }
      });

      // Pass 2 – annotate every item using the stable maps built above
      data = data.map(item => {
        if (item._is_grouped) return { ...item, _suggested_group_id: item._group_object?.id ?? '', _suggested_group_name: item._group_object?.group_name || itemFallbackName(item) };
        const key  = makeKey(item);
        if (key === null) return { ...item, _suggested_group_id: '', _suggested_group_name: '', _excluded_by_rules: true };

        // Multiple existing groups share this key → ambiguous, user must pick
        const matchingGroups = existingKeyAllGroups[key] || [];
        if (matchingGroups.length > 1) {
          return { ...item, _suggested_group_id: '', _suggested_group_name: '', _ambiguous_group: true, _matching_groups: matchingGroups };
        }

        const existingId   = existingKeyMap[key]?.id;
        const existingName = existingKeyMap[key]?.name;

        // Check max_days compatibility with the existing group.
        // If the item's date would push the group over the limit, fall through to a new group.
        if (existingId && !isDateCompatibleWithGroup(item, existingId)) {
          // Existing group rejects this item due to date spread — use windowed NEW-N
          const windowedId = itemWindowId[item.id];
          const sgId   = windowedId ?? '';
          const sgName = (windowedId ? (tempNameMap[windowedId] ?? '') : '') || itemFallbackName(item);
          return { ...item, _suggested_group_id: sgId, _suggested_group_name: sgName, _value_in_choice: false };
        }

        const sgId   = existingId   ?? itemWindowId[item.id] ?? tempIdMap[key]   ?? '';
        const sgName = existingName || (itemWindowId[item.id] ? (tempNameMap[itemWindowId[item.id]] ?? '') : '') || tempNameMap[key] || itemFallbackName(item);

        // Detect if this item carries a value_in field value not yet present in
        // the existing suggested group → user gets a choice instead of a silent add.
        let _value_in_choice = false;
        if (sgId && !String(sgId).startsWith('NEW-') && activeValueInFields.length > 0) {
          for (const field of activeValueInFields) {
            const itemVal = norm(String(item[field] ?? ''));
            const existing = existingGroupValueSets[key]?.[field];
            if (existing && itemVal && !existing.has(itemVal)) { _value_in_choice = true; break; }
          }
        }
        return { ...item, _suggested_group_id: sgId, _suggested_group_name: sgName, _value_in_choice };
      });

      // Debug: log key → id mapping so you can verify in the browser console
      console.debug('[Suggested Groups] activeRules:', activeRules.map(r => r.field));
      console.debug('[Suggested Groups] tempIdMap:', tempIdMap);
      // ────────────────────────────────────────────────────────────────────

      // Sort by suggested group ID so same-group items appear consecutively
      const sgSortKey = (id) => {
        if (!id && id !== 0) return [2, 0, ''];          // blank → last bucket
        const n = Number(id);
        if (!isNaN(n)) return [0, n, ''];                // real DB numeric ID → first bucket, numeric
        const m = String(id).match(/^NEW-(\d+)$/);
        if (m) return [1, parseInt(m[1], 10), ''];       // NEW-N → second bucket, numeric
        return [1, 0, String(id)];                       // fallback string
      };
      data.sort((a, b) => {
        const [bA, nA, sA] = sgSortKey(a._suggested_group_id);
        const [bB, nB, sB] = sgSortKey(b._suggested_group_id);
        if (bA !== bB) return bA - bB;
        if (nA !== nB) return nA - nB;
        return sA.localeCompare(sB);
      });

      // ── Bernardo Style Map lookup ────────────────────────────────────────
      // For each item, check if (style# + color + size) has a saved Bernardo
      // style# from a previous session. If found, attach as _map_bernardo_style.
      try {
        const lookupRes = await axios.post(`${API}/api/bernardo-style-map/bulk-lookup`, {
          vendor,
          items: data.map(i => ({
            id: i.id,
            customer_style_number: i.customer_style_number || '',
            color: i.color || '',
            size: i.size || '',
          })),
        });
        const styleMap = lookupRes.data?.results || {};
        if (Object.keys(styleMap).length > 0) {
          const enriched = data.map(item => {
            const key = `${String(item.customer_style_number || '').trim()}|${String(item.color || '').trim()}|${String(item.size || '').trim()}`;
            const mapped = styleMap[key] || '';
            return mapped ? { ...item, _map_bernardo_style: mapped } : item;
          });
          setLineItems(enriched);
          setFilteredItems(enriched);
        } else {
          setLineItems(data);
          setFilteredItems(data);
        }
      } catch {
        // Non-fatal — style map lookup failure should never block the main fetch
        setLineItems(data);
        setFilteredItems(data);
      }
      // ─────────────────────────────────────────────────────────────────────

      if (data.length === 0) {
        setSnackbar({ open: true, msg: 'No line items found for the selected date range.', severity: 'info' });
      }

      // 1. Gather all unique keys
      const allKeys = new Set();
      data.forEach(row => Object.keys(row).forEach(k => allKeys.add(k)));

      // 2. Remove internal/private keys
      const ignoredKeys = [
        'id', 'purchase_order_id', 'item_coordinates',
        '_group_object', '_is_grouped', '_suggested_groups',
        '_suggested_group_id', '_suggested_group_name', '_group_name',
      ];
      ignoredKeys.forEach(k => allKeys.delete(k));

      // 3. Build dynamic fields list (suggested cols first, then data)
      const dynamicFields = [
        { key: '_suggested_group_id',   label: 'Suggested Group ID'   },
        { key: '_suggested_group_name', label: 'Suggested Group Attributes' },
      ];
      const knownKeys = new Set(['_suggested_group_id', '_suggested_group_name']);

      FIELDS.forEach(f => { dynamicFields.push(f); knownKeys.add(f.key); });

      allKeys.forEach(k => {
        if (!knownKeys.has(k)) {
          const label = k.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
          dynamicFields.push({ key: k, label });
        }
      });

      // 4. Keep only columns that actually have data
      const alwaysShow = new Set(['_suggested_group_id', '_suggested_group_name']);
      const populated = dynamicFields.filter(f =>
        alwaysShow.has(f.key) ||
        data.some(row => {
          const v = row[f.key];
          return v !== null && v !== undefined && String(v).trim() !== '' && String(v).trim() !== '0';
        })
      );

      setVisibleFields(populated.length > 0 ? populated : dynamicFields);

      setSelectedColumns(prev => {
        const validKeys = new Set(populated.map(f => f.key));
        // Keep saved order but drop any key that doesn't exist in this fetch's data
        let cols = prev.filter(c => c !== '_is_grouped' && validKeys.has(c));
        // Ensure suggested cols are present at the front (in order)
        ['_suggested_group_name', '_suggested_group_id'].forEach(k => {
          if (!cols.includes(k)) cols = [k, ...cols];
        });
        return cols;
      });

      setFilterColumns([]);
      setFilterValues({});
      setLastFetchedAt(new Date());
    } catch { setError('Failed to fetch line items.'); } finally { setLoading(false); }
  };

  // When auto-init sets fromDate, trigger a fetch automatically
  useEffect(() => {
    if (pendingAutoFetchRef.current && fromDate) {
      pendingAutoFetchRef.current = false;
      handleFetchData();
    }
  }, [fromDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Re-derive bernardo style# suggestion when exclusions change mid-popup ──
  // When the user removes items from a group inside the batch dialog, the current
  // group's remaining items may have a _map_bernardo_style that wasn't reflected
  // in the queue entry's bernadoStyle (which was frozen at queue-build time).
  // Only fires when the field is still empty so it never clobbers a manual edit.
  useEffect(() => {
    if (!batchOpen) return;
    const current = batchQueue[batchIndex];
    if (!current || batchBernadoStyle) return;
    const activeItems = current.items.filter(i => !batchExcludedIds.has(i.id));
    const derived = activeItems.map(i => i._map_bernardo_style || '').find(s => s) || '';
    if (derived) {
      setBatchBernadoStyle(derived);
      setBatchStyleIsSuggested(true);
      setBatchQueue(prev => prev.map((e, i) => i === batchIndex
        ? { ...e, bernadoStyle: derived, bernadoStyleIsSuggested: true }
        : e
      ));
    }
  }, [batchExcludedIds, batchIndex, batchOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Apply ticked suggested groups (no page refresh — update state locally) ─
  const handleCreateSuggestedGroups = async () => {
    const tickedItems = lineItems.filter(i => tickedIds.has(i.id));
    if (!tickedItems.length) return;

    const buckets = {};
    tickedItems.forEach(item => {
      // If no suggestion ID exists, treat as a brand-new solo group keyed by item id
      const gid = item._suggested_group_id || `__solo_${item.id}`;
      const isNew = !gid || String(gid).startsWith('NEW-') || String(gid).startsWith('__solo_');
      if (!buckets[gid]) {
        const fallbackName = [item.customer_style_number, item.color, item.size, item.customer_po_number]
          .map(v => String(v ?? '').trim()).filter(Boolean).join('_') || `Group_${item.id}`;
        buckets[gid] = { id: gid, name: item._suggested_group_name || fallbackName, items: [], isExisting: !isNew, resolvedGroup: null };
      }
      buckets[gid].items.push(item);
    });

    setApplyingGroups(true);
    try {
      const localGroups = [...fetchedGroups];

      for (const bucket of Object.values(buckets)) {
        if (bucket.isExisting) {
          const existing = localGroups.find(g => String(g.id) === String(bucket.id));
          if (existing) {
            const eIds  = Array.isArray(existing.line_item_ids) ? existing.line_item_ids : [];
            const eData = Array.isArray(existing.group_data)    ? existing.group_data    : [];
            const nIds  = bucket.items.map(i => i.id).filter(id => !eIds.includes(id));
            const nData = bucket.items.filter(i => !eIds.includes(i.id));
            await axios.put(`${API}/api/line-item-groups/${existing.id}`, {
              group_name: existing.group_name, vendor, line_item_ids: [...eIds, ...nIds], group_data: [...eData, ...nData],
              bernardo_style_number: batchBernadoStyle.trim(),
              customer_item: batchCustomerItemOverride.trim() || batchCustomerItem?.customer_item || null,
              customer_item_is_new: !batchCustomerItemOverride.trim() && batchCustomerItem?.is_new === true,
            });
            bucket.resolvedGroup = { ...existing, line_item_ids: [...eIds, ...nIds], group_data: [...eData, ...nData] };
            const idx = localGroups.findIndex(g => g.id === existing.id);
            if (idx !== -1) localGroups[idx] = bucket.resolvedGroup;
          }
        } else {
          const res = await axios.post(`${API}/api/line-item-groups`, {
            group_name: bucket.name, vendor,
            line_item_ids: bucket.items.map(i => i.id),
            group_data: bucket.items,
            custom_rules: allRules.filter(r => enabledRuleIds.has(r.id)),
            bernardo_style_number: batchBernadoStyle.trim(),
            customer_item: batchCustomerItemOverride.trim() || batchCustomerItem?.customer_item || null,
          });
          bucket.resolvedGroup = res.data?.data || { id: bucket.id, group_name: bucket.name, line_item_ids: bucket.items.map(i => i.id), group_data: bucket.items };
          localGroups.push(bucket.resolvedGroup);
        }
      }

      // Mark applied items as grouped in local state — no re-fetch
      const appliedIds = new Set(tickedItems.map(i => i.id));
      const patch = (items) => items.map(item => {
        if (!appliedIds.has(item.id)) return item;
        const bucket = Object.values(buckets).find(b => b.items.some(i => i.id === item.id));
        if (!bucket?.resolvedGroup) return item;
        return { ...item, _is_grouped: true, _group_name: bucket.resolvedGroup.group_name, _group_object: bucket.resolvedGroup, _suggested_group_id: bucket.resolvedGroup.id ?? '', _suggested_group_name: bucket.resolvedGroup.group_name ?? '' };
      });

      setLineItems(patch);
      setFilteredItems(patch);
      setFetchedGroups(localGroups);
      setTickedIds(prev => { const n = new Set(prev); appliedIds.forEach(id => n.delete(id)); return n; });
      setCrossedIds(prev => { const n = new Set(prev); appliedIds.forEach(id => n.delete(id)); return n; });

      const count = Object.keys(buckets).length;
      setSnackbar({ open: true, msg: `${count} group${count !== 1 ? 's' : ''} applied!`, severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, msg: e.response?.data?.detail || 'Failed to apply groups.', severity: 'error' });
    } finally {
      setApplyingGroups(false);
    }
  };

  // ── Suggest Bernardo Style # for a new group based on existing groups ────
  // If any existing group that already has a bernardo_style_number contains items
  // with the same customer_style_number + color + size as the new group's items,
  // that bernardo_style_number is returned as a suggestion.
  const suggestBernadoStyle = (items, groups) => {
    if (!items?.length || !groups?.length) return '';
    const n = v => String(v ?? '').trim().toLowerCase();
    const sty = n(items[0].customer_style_number);
    const col = n(items[0].color);
    const siz = n(items[0].size);
    if (!sty) return '';
    for (const g of groups) {
      if (!g.bernardo_style_number) continue;
      const gItems = Array.isArray(g.group_data) ? g.group_data : [];
      const hit = gItems.some(gi =>
        n(gi.customer_style_number) === sty &&
        n(gi.color) === col &&
        n(gi.size) === siz
      );
      if (hit) return g.bernardo_style_number;
    }
    return '';
  };

  // ── Batch review mode ────────────────────────────────────────────────────
  const openBatchMode = (specificGid = null) => {
    // Always exclude crossed-out items — they were explicitly excluded by the user
    const source = specificGid
      ? filteredItems.filter(i => i._suggested_group_id === specificGid && !crossedIds.has(i.id))
      : filteredItems.filter(i => !i._is_grouped && !crossedIds.has(i.id));
    if (!source.length) return;

    // Tick items: when opening a specific group, MERGE into existing ticks so
    // other already-ticked groups are not lost. When opening all (Check All),
    // replace everything and clear crosses.
    let mergedTickedIds;
    if (specificGid) {
      // Merge but never include anything that is crossed out
      mergedTickedIds = new Set([
        ...[...tickedIds].filter(id => !crossedIds.has(id)),
        ...source.map(i => i.id),
      ]);
      setTickedIds(mergedTickedIds);
      // Don't touch crossedIds — the user's crosses on other items stay intact
    } else {
      mergedTickedIds = new Set(source.map(i => i.id));
      setTickedIds(mergedTickedIds);
      setCrossedIds(new Set());
    }

    // Build queue from ALL currently-ticked ungrouped items (not just `source`),
    // explicitly skipping anything crossed out.
    const allTickedItems = filteredItems.filter(
      i => !i._is_grouped && mergedTickedIds.has(i.id) && !crossedIds.has(i.id)
    );

    const groupMap = {};
    allTickedItems.forEach(item => {
      const gid = item._suggested_group_id || `__solo_${item.id}`;
      if (!groupMap[gid]) {
        const nameFallback = [item.customer_style_number, item.color, item.size]
          .map(v => String(v ?? '').trim()).filter(Boolean).join('_') || `Group_${item.id}`;
        groupMap[gid] = { gid, name: item._suggested_group_name || nameFallback, items: [] };
      }
      groupMap[gid].items.push(item);
    });

    // Attach bernadoStyle to every entry so edits survive navigation
    const queue = Object.values(groupMap).sort((a, b) => {
      const aNew = String(a.gid).startsWith('NEW-') || String(a.gid).startsWith('__solo_');
      const bNew = String(b.gid).startsWith('NEW-') || String(b.gid).startsWith('__solo_');
      if (!aNew && bNew) return -1;
      if (aNew && !bNew) return 1;
      const aNum = parseInt(String(a.gid).replace(/\D/g, '') || '0');
      const bNum = parseInt(String(b.gid).replace(/\D/g, '') || '0');
      return aNum - bNum;
    }).map(entry => {
      const isExisting = entry.gid && !String(entry.gid).startsWith('NEW-') && !String(entry.gid).startsWith('__solo_');
      const existing = isExisting ? fetchedGroups.find(g => String(g.id) === String(entry.gid)) : null;
      const existingStyle = existing?.bernardo_style_number || '';
      // Map-based suggestion: look up (style# + color + size) from any item in this entry.
      // Scan all items so we don't miss a hit when items[0] lacks the field.
      const firstItem = entry.items[0];
      const mapStyle = entry.items.map(i => i._map_bernardo_style || '').find(s => s) || '';
      return {
        ...entry,
        bernadoStyle: existingStyle || mapStyle,
        bernadoStyleIsSuggested: !existingStyle && !!mapStyle,
        // Keep both separately so the dialog can show both when they differ
        bernadoStyleFromGroup: existingStyle,
        bernadoStyleFromMap: mapStyle,
      };
    });

    setBatchQueue(queue);
    setBatchIndex(0);
    setConfirmedGids(new Set());
    setBatchExcludedIds(new Set());
    setBatchGroupName(queue[0]?.name || '');
    setBatchBernadoStyle(queue[0]?.bernadoStyle || '');
    setBatchStyleIsSuggested(queue[0]?.bernadoStyleIsSuggested || false);
    setBatchCustomerItem(null);
    setBatchCustomerItemOverride('');
    setBatchOpen(true);
  };

  const advanceBatch = (queue, idx, confirmedGid) => {
    // Build the updated confirmed set (if a gid was just confirmed)
    const updated = confirmedGid
      ? new Set([...confirmedGids, String(confirmedGid)])
      : new Set(confirmedGids);
    if (confirmedGid) setConfirmedGids(updated);

    // All groups done?
    if (updated.size >= queue.length) {
      setBatchOpen(false);
      setBatchQueue([]);
      setBatchIndex(0);
      setBatchBernadoStyle('');
      setBatchCustomerItem(null);
      setBatchCustomerItemOverride('');
      setConfirmedGids(new Set());
      setSnackbar({ open: true, msg: 'All groups applied!', severity: 'success' });
      return;
    }

    // Find next unconfirmed entry after current position, then wrap around
    let nextIdx = queue.findIndex((entry, i) => i > idx && !updated.has(String(entry.gid)));
    if (nextIdx === -1) {
      nextIdx = queue.findIndex((entry, i) => i !== idx && !updated.has(String(entry.gid)));
    }
    if (nextIdx === -1) {
      // Shouldn't happen (size check above), but guard
      setBatchOpen(false);
      setBatchQueue([]);
      setBatchIndex(0);
      setBatchBernadoStyle('');
      setBatchCustomerItem(null);
      setBatchCustomerItemOverride('');
      setConfirmedGids(new Set());
      setSnackbar({ open: true, msg: 'All groups applied!', severity: 'success' });
      return;
    }
    setBatchIndex(nextIdx);
    setBatchExcludedIds(new Set());
    setBatchGroupName(queue[nextIdx]?.name || '');
    setBatchBernadoStyle(queue[nextIdx]?.bernadoStyle || '');
    setBatchStyleIsSuggested(queue[nextIdx]?.bernadoStyleIsSuggested || false);
    // Clear Customer Item # so a manual edit from the previous group does not stick
    setBatchCustomerItem(null);
    setBatchCustomerItemOverride('');
  };

  // Jump to any unconfirmed queue index (dot click or arrow buttons)
  const navigateToBatch = (targetIdx) => {
    if (batchConfirming) return;
    if (targetIdx < 0 || targetIdx >= batchQueue.length) return;
    // Cannot navigate to an already-confirmed entry
    if (confirmedGids.has(String(batchQueue[targetIdx]?.gid))) return;
    // Persist current edits back into the queue before leaving
    setBatchQueue(prev => prev.map((entry, i) =>
      i === batchIndex ? { ...entry, name: batchGroupName, bernadoStyle: batchBernadoStyle } : entry
    ));
    const target = batchQueue[targetIdx];
    setBatchIndex(targetIdx);
    setBatchExcludedIds(new Set());
    setBatchGroupName(target?.name || '');
    const nextStyle = target?.bernadoStyle || '';
    setBatchBernadoStyle(nextStyle);
    setBatchStyleIsSuggested(target?.bernadoStyleIsSuggested || false);
    // Clear Customer Item # — i2 preview will refill via useEffect
    setBatchCustomerItem(null);
    setBatchCustomerItemOverride('');
  };

  const handleBatchConfirm = async () => {
    const current = batchQueue[batchIndex];
    if (!current) return;
    setBatchConfirming(true);
    try {
      const { gid } = current;
      // Respect per-item exclusions the user made inside the dialog
      const items = current.items.filter(i => !batchExcludedIds.has(i.id));
      if (items.length === 0) {
        setBatchConfirming(false);
        advanceBatch(batchQueue, batchIndex, current.gid);
        return;
      }
      const groupNameToUse = batchGroupName.trim() || current.name || `Group_${gid}`;
      const isExisting = gid && !String(gid).startsWith('NEW-') && !String(gid).startsWith('__solo_');
      // Use the actual vendor from the line items, not the dropdown (which is just a filter).
      // This matters for sub-vendors: items uploaded as american_eagle_mens must use BAM prefix.
      const itemVendor = items[0]?.vendor || vendor;

      let resolvedGroup;
      if (isExisting) {
        const existing = fetchedGroups.find(g => String(g.id) === String(gid));
        if (existing) {
          const eIds  = Array.isArray(existing.line_item_ids) ? existing.line_item_ids : [];
          const eData = Array.isArray(existing.group_data)    ? existing.group_data    : [];
          const nIds  = items.map(i => i.id).filter(id => !eIds.includes(id));
          const nData = items.filter(i => !eIds.includes(i.id));
          await axios.put(`${API}/api/line-item-groups/${existing.id}`, {
            group_name: existing.group_name, vendor: itemVendor, line_item_ids: [...eIds, ...nIds], group_data: [...eData, ...nData],
            bernardo_style_number: batchBernadoStyle.trim() || existing.bernardo_style_number || '',
            customer_item: batchCustomerItemOverride.trim() || batchCustomerItem?.customer_item || null,
            customer_item_is_new: !batchCustomerItemOverride.trim() && batchCustomerItem?.is_new === true,
          });
          resolvedGroup = { ...existing, line_item_ids: [...eIds, ...nIds], group_data: [...eData, ...nData] };
          setFetchedGroups(prev => prev.map(g => g.id === existing.id ? resolvedGroup : g));
        }
      } else {
        const res = await axios.post(`${API}/api/line-item-groups`, {
          group_name: groupNameToUse, vendor: itemVendor,
          line_item_ids: items.map(i => i.id),
          group_data: items,
          custom_rules: allRules.filter(r => enabledRuleIds.has(r.id)),
          bernardo_style_number: batchBernadoStyle.trim(),
          customer_item: batchCustomerItemOverride.trim() || batchCustomerItem?.customer_item || null,
        });
        resolvedGroup = res.data?.data || { group_name: groupNameToUse, line_item_ids: items.map(i => i.id), group_data: items };
        setFetchedGroups(prev => [...prev, resolvedGroup]);
      }

      let updatedQueue = batchQueue;
      if (resolvedGroup) {
        const appliedIds = new Set(items.map(i => i.id));
        const originalGid = String(current.gid); // temp id e.g. "NEW-3" or existing id
        const patch = arr => arr.map(item => {
          if (appliedIds.has(item.id)) {
            // Item was added to the group — mark as grouped
            return {
              ...item, _is_grouped: true, _group_name: resolvedGroup.group_name,
              _group_object: resolvedGroup,
              _suggested_group_id: resolvedGroup.id ?? '',
              _suggested_group_name: resolvedGroup.group_name ?? '',
            };
          }
          // Item was excluded from this batch but shares the same suggested group key —
          // update its suggestion to point to the real group so it shows green next round.
          if (String(item._suggested_group_id) === originalGid) {
            return {
              ...item,
              _suggested_group_id: resolvedGroup.id ?? '',
              _suggested_group_name: resolvedGroup.group_name ?? '',
            };
          }
          return item;
        });
        setLineItems(patch);
        setFilteredItems(patch);
        setTickedIds(prev => { const n = new Set(prev); appliedIds.forEach(id => n.delete(id)); return n; });
        setCrossedIds(prev => { const n = new Set(prev); appliedIds.forEach(id => n.delete(id)); return n; });

        // ── Persist Style# + Color + Size → Bernardo Style# mapping ──────────
        const bsn = batchBernadoStyle.trim();
        if (bsn) {
          const entries = items
            .filter(i => String(i.customer_style_number || '').trim())
            .map(i => ({
              customer_style_number: String(i.customer_style_number || '').trim(),
              color: String(i.color || '').trim(),
              size:  String(i.size  || '').trim(),
              bernardo_style_number: bsn,
            }));
          if (entries.length > 0) {
            axios.post(`${API}/api/bernardo-style-map/upsert`, { vendor, entries })
              .catch(err => console.warn('[StyleMap] upsert failed (non-fatal):', err));

            // Build set of (style#|color|size) keys just mapped
            const mappedKeys = new Set(entries.map(e => `${e.customer_style_number}|${e.color}|${e.size}`));

            // Update remaining batch queue entries so the next entry auto-fills
            updatedQueue = batchQueue.map((entry, i) => {
              if (i === batchIndex || entry.bernadoStyle) return entry;
              const k = `${String(entry.items[0]?.customer_style_number||'').trim()}|${String(entry.items[0]?.color||'').trim()}|${String(entry.items[0]?.size||'').trim()}`;
              return mappedKeys.has(k) ? { ...entry, bernadoStyle: bsn, bernadoStyleIsSuggested: true } : entry;
            });
            setBatchQueue(updatedQueue);

            // Update lineItems / filteredItems so future batch builds pick it up too
            const patchItems = fn => fn.map(item => {
              const k = `${String(item.customer_style_number||'').trim()}|${String(item.color||'').trim()}|${String(item.size||'').trim()}`;
              return mappedKeys.has(k) && !item._map_bernardo_style ? { ...item, _map_bernardo_style: bsn } : item;
            });
            setLineItems(patchItems);
            setFilteredItems(patchItems);
          }
        }
        // ─────────────────────────────────────────────────────────────────────
      }
      advanceBatch(updatedQueue, batchIndex, current.gid);
    } catch (e) {
      setSnackbar({ open: true, msg: e.response?.data?.detail || 'Failed to apply group.', severity: 'error' });
    } finally {
      setBatchConfirming(false);
    }
  };
  // ─────────────────────────────────────────────────────────────────────────
  // ─────────────────────────────────────────────────────────────────────────

  // Reset visible fields when vendor changes is handled directly in the Select onChange

  // ── Auto-scroll helpers for column drag-and-drop ────────────────────────
  const startAutoScroll = (direction) => {
    if (autoScrollRef.current) return;
    autoScrollRef.current = setInterval(() => {
      const el = tableContainerRef.current;
      if (el) el.scrollLeft += direction * 18;
    }, 16);
  };
  const stopAutoScroll = () => {
    if (autoScrollRef.current) { clearInterval(autoScrollRef.current); autoScrollRef.current = null; }
  };

  const handleColDrop = (dragKey, dropKey) => {
    if (!dragKey || !dropKey || dragKey === dropKey) return;
    setSelectedColumns(prev => {
      const cols = [...prev];
      const from = cols.indexOf(dragKey);
      const to   = cols.indexOf(dropKey);
      if (from === -1 || to === -1) return prev;
      cols.splice(from, 1);
      cols.splice(to, 0, dragKey);
      localStorage.setItem('lineItemCols', JSON.stringify(cols));
      return cols;
    });
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const toggleAll = () => {
    if (selectedIds.size === displayItems.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(displayItems.map(i => i.id)));
  };

  const openGroupDialog = () => {
    const selected = filteredItems.filter(i => selectedIds.has(i.id));
    const violations = validateRules(selected, currentRules);
    setGroupViolations(violations);
    setGroupName('');
    setBernadoStyleForGroup('');
    setGroupDialogOpen(true);
  };

  const handleCreateGroup = async () => {
    if (!groupName.trim()) return;
    const selected = filteredItems.filter(i => selectedIds.has(i.id));
    try {
      setGrouping(true);
      await axios.post(`${API}/api/line-item-groups`, {
        group_name: groupName.trim(),
        vendor,
        line_item_ids: selected.map(i => i.id),
        group_data: selected,
        custom_rules: currentRules,
        bernardo_style_number: bernadoStyleForGroup.trim(),
      });
      setSnackbar({ open: true, msg: `Group "${groupName}" created successfully!`, severity: 'success' });
      setGroupDialogOpen(false); setSelectedIds(new Set()); setGroupName('');

      // Persist Style# + Color + Size → Bernardo Style# mapping
      const bsn = bernadoStyleForGroup.trim();
      if (bsn) {
        const entries = selected
          .filter(i => String(i.customer_style_number || '').trim())
          .map(i => ({
            customer_style_number: String(i.customer_style_number || '').trim(),
            color: String(i.color || '').trim(),
            size:  String(i.size  || '').trim(),
            bernardo_style_number: bsn,
          }));
        if (entries.length > 0) {
          axios.post(`${API}/api/bernardo-style-map/upsert`, { vendor, entries })
            .catch(err => console.warn('[StyleMap] upsert failed (non-fatal):', err));

          // Immediately reflect the new mapping in-memory so remaining items auto-fill
          const mappedKeys = new Set(entries.map(e => `${e.customer_style_number}|${e.color}|${e.size}`));
          const patchItems = fn => fn.map(item => {
            const k = `${String(item.customer_style_number||'').trim()}|${String(item.color||'').trim()}|${String(item.size||'').trim()}`;
            return mappedKeys.has(k) && !item._map_bernardo_style ? { ...item, _map_bernardo_style: bsn } : item;
          });
          setLineItems(patchItems);
          setFilteredItems(patchItems);
        }
      }
    } catch (e) {
      setSnackbar({ open: true, msg: e.response?.data?.detail || 'Failed to create group.', severity: 'error' });
    } finally { setGrouping(false); }
  };

  const openAddToGroup = async () => {
    setAtgSelectedGroup(null);
    setAtgViolations([]);
    setAtgOpen(true);
    try {
      setAtgLoading(true);
      const res = await axios.get(`${API}/api/line-item-groups?vendor=${vendor}`);
      setExistingGroups(res.data?.data || []);
    } catch { setSnackbar({ open: true, msg: 'Failed to load groups.', severity: 'error' }); }
    finally { setAtgLoading(false); }
  };

  const handleAtgGroupSelect = (group) => {
    setAtgSelectedGroup(group);
    // Validate merge: existing group_data + newly selected items
    const newItems = filteredItems.filter(i => selectedIds.has(i.id));
    const existingItems = Array.isArray(group.group_data) ? group.group_data : [];
    const merged = [...existingItems, ...newItems];
    const rulesToUse = group.custom_rules && group.custom_rules.length > 0 ? group.custom_rules : FIXED_RULES;
    setAtgViolations(validateRules(merged, rulesToUse));
  };

  const handleAddToGroup = async () => {
    if (!atgSelectedGroup) return;
    const newItems = filteredItems.filter(i => selectedIds.has(i.id));
    const existingIds = Array.isArray(atgSelectedGroup.line_item_ids) ? atgSelectedGroup.line_item_ids : [];
    const existingData = Array.isArray(atgSelectedGroup.group_data) ? atgSelectedGroup.group_data : [];
    // Avoid duplicates
    const newIds = newItems.map(i => i.id).filter(id => !existingIds.includes(id));
    const newData = newItems.filter(i => !existingIds.includes(i.id));
    const mergedIds  = [...existingIds, ...newIds];
    const mergedData = [...existingData, ...newData];
    try {
      setAtgSaving(true);
      await axios.put(`${API}/api/line-item-groups/${atgSelectedGroup.id}`, {
        group_name: atgSelectedGroup.group_name,
        line_item_ids: mergedIds,
        group_data: mergedData,
        bernardo_style_number: atgSelectedGroup.bernardo_style_number || '',
      });
      setSnackbar({ open: true, msg: `${newIds.length} item(s) added to "${atgSelectedGroup.group_name}".`, severity: 'success' });
      setAtgOpen(false);
      setSelectedIds(new Set());
    } catch (e) {
      setSnackbar({ open: true, msg: e.response?.data?.detail || 'Failed to update group.', severity: 'error' });
    } finally { setAtgSaving(false); }
  };

  const handleMergeSuggestedGroup = async (group) => {
    if (!suggestedItem) return;
    try {
      setMergingGroupId(group.id);
      const existingIds = Array.isArray(group.line_item_ids) ? group.line_item_ids : [];
      const existingData = Array.isArray(group.group_data) ? group.group_data : [];
      const mergedIds = [...existingIds, suggestedItem.id];
      const mergedData = [...existingData, suggestedItem];
      
      await axios.put(`${API}/api/line-item-groups/${group.id}`, {
        group_name: group.group_name,
        line_item_ids: mergedIds,
        group_data: mergedData,
        bernardo_style_number: group.bernardo_style_number || '',
      });

      setSnackbar({ open: true, msg: `Item successfully merged into "${group.group_name}"!`, severity: 'success' });
      setSuggestedItem(null);
      handleFetchData();
    } catch (e) {
      setSnackbar({ open: true, msg: e.response?.data?.detail || 'Failed to merge item into group.', severity: 'error' });
    } finally {
      setMergingGroupId(null);
    }
  };

  const selectedCount = selectedIds.size;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              letterSpacing: '-0.025em',
              mb: 0.5,
              fontSize: { xs: '1.5rem', sm: '1.875rem' },
              background: isDark
                ? 'linear-gradient(135deg, #e2e8f0 0%, #94a3b8 100%)'
                : 'linear-gradient(135deg, #1e293b 0%, #4f46e5 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Line Item Filtering
          </Typography>
          <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
            Fetch and group line items by customer and date range.
          </Typography>
        </Box>

        {/* ── Main content ── */}
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', minWidth: 0 }}>

        {/* ── Fetch + Rules Card ── */}
        <Paper elevation={0} sx={{ p: 2, mb: 2, flexShrink: 0, borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>

          {/* Row 1 — Controls */}
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 150, flex: '1 1 150px' }}>
              <InputLabel>Customer</InputLabel>
              <Select
                value={vendor}
                label="Customer"
                onChange={e => {
                  setVendor(e.target.value);
                  sessionStorage.setItem('li_vendor', e.target.value);
                  setSelectedIds(new Set());
                  setTickedIds(new Set());
                  setCrossedIds(new Set());
                }}
              >
                {VENDORS.map(v => <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>)}
              </Select>
            </FormControl>
            <Tooltip title="Date field is fixed per customer based on the master mapping" arrow placement="top">
              <FormControl size="small" disabled sx={{ minWidth: 170, flex: '1 1 170px' }}>
                <InputLabel>Date Field</InputLabel>
                <Select
                  value={dateField}
                  label="Date Field"
                  IconComponent={() => null}
                  sx={{ '& .MuiSelect-select': { paddingRight: '14px !important' } }}
                >
                  {FIELDS.filter(f => f.type === 'date').map(f => {
                    const vendorAlias = VENDOR_DATE_FIELD_LABELS[vendor]?.[f.key];
                    return (
                      <MenuItem key={f.key} value={f.key}>
                        {vendorAlias
                          ? <>{vendorAlias} <span style={{ fontSize: '0.75em', opacity: 0.55, marginLeft: 4 }}>({f.label})</span></>
                          : f.label}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Tooltip>
            <Box sx={{ flex: '1 1 145px', minWidth: 145, position: 'relative' }}>
              <DatePicker
                label="From Date"
                value={fromDate}
                onChange={setFromDate}
                maxDate={toDate || undefined}
                slotProps={{
                  textField: { size: 'small', fullWidth: true },
                  field: { clearable: true },
                }}
              />
            </Box>
            <Box sx={{ flex: '1 1 145px', minWidth: 145, position: 'relative' }}>
              <DatePicker
                label="To Date"
                value={toDate}
                onChange={setToDate}
                minDate={fromDate || undefined}
                slotProps={{
                  textField: { size: 'small', fullWidth: true },
                  field: { clearable: true },
                }}
              />
            </Box>
            {(fromDate || toDate) && (
              <Tooltip title="Clear both dates" arrow>
                <IconButton
                  size="small"
                  onClick={() => { setFromDate(null); setToDate(null); }}
                  sx={{
                    alignSelf: 'center',
                    color: 'text.disabled',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '6px',
                    p: 0.5,
                    flexShrink: 0,
                    '&:hover': { color: 'error.main', borderColor: 'error.main', bgcolor: 'rgba(239,68,68,0.06)' },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {/* Divider */}
          <Box sx={{ my: 1.75, borderTop: '1px solid', borderColor: 'divider' }} />

          {/* Row 2 — Grouping Rules (unified rule-sets view) */}
          <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'text.secondary' }}>
                  Grouping Rules
                </Typography>
                <Tooltip title="Each rule set is an independent grouping profile. Click a rule set to enable/disable it. Use Configure to add/edit rules inside each set." arrow>
                  <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', cursor: 'default', lineHeight: 1 }}>ⓘ</Typography>
                </Tooltip>
                {rulesLoading && <CircularProgress size={10} sx={{ color: '#818cf8' }} />}
              </Box>
              <Button
                size="small"
                startIcon={<SettingsIcon sx={{ fontSize: '13px !important' }} />}
                onClick={() => setRuleConfigOpen(true)}
                sx={{
                  height: 22, fontSize: '0.7rem', px: 1.25, py: 0, minWidth: 0,
                  color: '#818cf8', border: '1px solid rgba(99,102,241,0.3)',
                  borderRadius: '6px',
                  '&:hover': { backgroundColor: 'rgba(99,102,241,0.08)', borderColor: 'rgba(99,102,241,0.6)' },
                }}
              >
                Configure
              </Button>
            </Box>

            {/* Rule set conflict warning banner */}
            {(() => {
              const conflicts = detectRuleSetConflicts(ruleSets);
              if (!conflicts.length) return null;
              return (
                <Box sx={{
                  mb: 0.75, px: 1.25, py: 0.75, borderRadius: '10px',
                  border: '1px solid rgba(245,158,11,0.4)',
                  bgcolor: 'rgba(245,158,11,0.07)',
                  display: 'flex', flexDirection: 'column', gap: 0.4,
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                    <WarningAmberIcon sx={{ fontSize: '0.95rem', color: '#f59e0b', flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#f59e0b' }}>
                      Rule Set Conflict{conflicts.length > 1 ? 's' : ''} Detected
                    </Typography>
                  </Box>
                  {conflicts.map((c, ci) => (
                    <Typography key={ci} sx={{ fontSize: '0.68rem', color: 'text.secondary', pl: 1.6, lineHeight: 1.5 }}>
                      <strong style={{ color: '#f59e0b' }}>{c.rs1Name}</strong> vs <strong style={{ color: '#f59e0b' }}>{c.rs2Name}</strong>
                      {c.fieldConflicts.map((fc, fi) => (
                        fc.isDuplicate
                          ? <span key={fi}> — identical match rules, second set may never be reached</span>
                          : <span key={fi}> — overlapping <em>{fc.field}</em> values: [{fc.overlappingValues.slice(0, 3).join(', ')}{fc.overlappingValues.length > 3 ? `, +${fc.overlappingValues.length - 3} more` : ''}]</span>
                      ))}
                    </Typography>
                  ))}
                </Box>
              );
            })()}

            {/* One card per rule set */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {ruleSets.length === 0 && (
                <Box sx={{
                  px: 1.25, py: 1, borderRadius: '10px',
                  border: '1px dashed', borderColor: 'divider',
                  bgcolor: 'rgba(0,0,0,0.01)',
                }}>
                  <Typography sx={{ fontSize: '0.72rem', color: 'text.disabled', fontStyle: 'italic' }}>
                    No grouping rules configured for this customer yet — click <strong>Configure</strong> to add rule sets.
                  </Typography>
                </Box>
              )}
              {ruleSets.map(rs => {
                const activeRules  = rs.rules.filter(r => r.enabled !== false);
                const rsConflicts  = detectRuleSetConflicts(ruleSets).filter(c => c.rs1Id === rs.id || c.rs2Id === rs.id);
                const hasConflict  = rs.enabled && rsConflicts.length > 0;
                const isExpanded   = expandedRuleSets.has(rs.id);

                return (
                  <Box
                    key={rs.id}
                    sx={{
                      border: '1px solid',
                      borderColor: hasConflict ? 'rgba(245,158,11,0.45)' : rs.enabled ? 'rgba(99,102,241,0.3)' : 'rgba(148,163,184,0.15)',
                      borderRadius: '10px',
                      px: 1.25, py: 0.6,
                      bgcolor: hasConflict ? 'rgba(245,158,11,0.05)' : rs.enabled ? 'rgba(99,102,241,0.04)' : 'rgba(0,0,0,0.01)',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    {/* ── Collapsed header row (always visible) ── */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>

                      {/* Dot */}
                      <Box sx={{ width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
                        bgcolor: hasConflict ? '#f59e0b' : rs.enabled ? '#818cf8' : 'rgba(148,163,184,0.4)' }} />

                      {/* Name + rule count — clicking toggles ON/OFF */}
                      <Box
                        onClick={() => setRuleSets(prev => prev.map(r => r.id === rs.id ? { ...r, enabled: !r.enabled } : r))}
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.4, cursor: 'pointer', userSelect: 'none', flex: 1, minWidth: 0 }}
                      >
                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: hasConflict ? '#f59e0b' : rs.enabled ? '#818cf8' : 'text.disabled', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {rs.name}
                        </Typography>
                        <Typography sx={{ fontSize: '0.66rem', color: 'text.disabled', flexShrink: 0 }}>
                          ({activeRules.length})
                        </Typography>
                        {hasConflict && (
                          <Tooltip title={rsConflicts.map(c => `Conflicts with: ${c.rs1Id === rs.id ? c.rs2Name : c.rs1Name}`).join(' · ')}>
                            <WarningAmberIcon sx={{ fontSize: '0.82rem', color: '#f59e0b', flexShrink: 0 }} />
                          </Tooltip>
                        )}
                      </Box>

                      {/* ON/OFF label */}
                      <Typography
                        onClick={() => setRuleSets(prev => prev.map(r => r.id === rs.id ? { ...r, enabled: !r.enabled } : r))}
                        sx={{ fontSize: '0.63rem', fontWeight: 700, flexShrink: 0, cursor: 'pointer', userSelect: 'none',
                          color: hasConflict ? '#f59e0b' : rs.enabled ? '#818cf8' : 'text.disabled' }}
                      >
                        {rs.enabled ? 'ON' : 'OFF'}
                      </Typography>

                      {/* Move up / move down */}
                      {(() => {
                        const idx = ruleSets.findIndex(r => r.id === rs.id);
                        return (
                          <Box sx={{ display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
                            <IconButton
                              size="small" disabled={idx === 0}
                              onClick={() => setRuleSets(prev => {
                                const arr = [...prev];
                                [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
                                return arr;
                              })}
                              sx={{ p: 0, color: idx === 0 ? 'transparent' : 'text.disabled', '&:hover': { color: 'text.primary' } }}
                            >
                              <ExpandLessIcon sx={{ fontSize: '0.85rem' }} />
                            </IconButton>
                            <IconButton
                              size="small" disabled={idx === ruleSets.length - 1}
                              onClick={() => setRuleSets(prev => {
                                const arr = [...prev];
                                [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
                                return arr;
                              })}
                              sx={{ p: 0, color: idx === ruleSets.length - 1 ? 'transparent' : 'text.disabled', '&:hover': { color: 'text.primary' } }}
                            >
                              <ExpandMoreIcon sx={{ fontSize: '0.85rem' }} />
                            </IconButton>
                          </Box>
                        );
                      })()}

                      {/* Expand/collapse chevron */}
                      <IconButton
                        size="small"
                        onClick={() => toggleRuleSetExpanded(rs.id)}
                        sx={{ p: 0.2, flexShrink: 0, color: 'text.disabled', '&:hover': { color: 'text.primary' } }}
                      >
                        {isExpanded
                          ? <ExpandLessIcon sx={{ fontSize: '0.95rem' }} />
                          : <ExpandMoreIcon sx={{ fontSize: '0.95rem' }} />}
                      </IconButton>
                    </Box>

                    {/* ── Expanded: rule chips ── */}
                    <Collapse in={isExpanded} timeout={150}>
                      <Box sx={{ mt: 0.75 }}>
                        {activeRules.length > 0 ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {activeRules.map(rule => {
                              const typeColor =
                                rule.type === 'match_all' ? { bg: 'rgba(99,102,241,0.08)', color: rs.enabled ? '#818cf8' : 'text.disabled', border: 'rgba(99,102,241,0.2)' } :
                                rule.type === 'value_in'  ? { bg: 'rgba(16,185,129,0.08)',  color: rs.enabled ? '#10b981'  : 'text.disabled', border: 'rgba(16,185,129,0.2)'  } :
                                rule.type === 'max_days'  ? { bg: 'rgba(245,158,11,0.08)',  color: rs.enabled ? '#f59e0b'  : 'text.disabled', border: 'rgba(245,158,11,0.2)'  } :
                                                            { bg: 'rgba(148,163,184,0.08)', color: 'text.secondary',                          border: 'rgba(148,163,184,0.2)' };
                              const isValueIn = rule.type === 'value_in';
                              return (
                                <Box
                                  key={rule.id || rule.name}
                                  sx={{
                                    px: 0.75, py: 0.2, borderRadius: '6px',
                                    border: `1px solid ${typeColor.border}`,
                                    bgcolor: typeColor.bg,
                                    display: 'flex', alignItems: 'center', gap: 0.4,
                                    ...(isValueIn && {
                                      cursor: 'pointer',
                                      '&:hover': { borderColor: '#10b981', bgcolor: 'rgba(16,185,129,0.14)' },
                                      '&:hover .vi-edit-icon': { opacity: 1 },
                                    }),
                                  }}
                                  onClick={isValueIn ? () => {
                                    setValueInEditValues([...(rule.values || [])]);
                                    setValueInEditInput('');
                                    setValueInEditDialog({ rsId: rs.id, rule });
                                  } : undefined}
                                >
                                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: typeColor.color, lineHeight: 1.4 }}>
                                    {rule.name}
                                    {isValueIn && rule.values?.length > 0 && (
                                      <span style={{ fontWeight: 400, opacity: 0.75 }}> [{rule.values.join(' OR ')}]</span>
                                    )}
                                    {rule.type === 'max_days' && (
                                      <span style={{ fontWeight: 400, opacity: 0.75 }}> ≤{rule.max}d</span>
                                    )}
                                  </Typography>
                                  {isValueIn && (
                                    <EditIcon
                                      className="vi-edit-icon"
                                      sx={{ fontSize: '0.7rem', color: '#10b981', opacity: 0, transition: 'opacity 0.15s', flexShrink: 0 }}
                                    />
                                  )}
                                </Box>
                              );
                            })}
                          </Box>
                        ) : (
                          <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled', fontStyle: 'italic' }}>
                            No rules — click Configure to add some.
                          </Typography>
                        )}
                      </Box>
                    </Collapse>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Row 3 — Fetch button + grouped toggle + post-fetch actions */}
          <Box sx={{ mt: 1.75, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            {(() => {
              const noRules = !ruleSets.some(rs => rs.enabled && rs.rules.some(r => r.enabled !== false));
              const canFetch = !loading && !noRules;
              return (
                <>
                  <Button
                    variant="contained"
                    onClick={handleFetchData}
                    disabled={!canFetch}
                    sx={{
                      height: 36, px: 2.5, fontSize: '0.8125rem', fontWeight: 600,
                      background: canFetch ? '#4a4f5a' : undefined,
                      '&:hover': { background: canFetch ? '#373c45' : undefined },
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Fetch Data'}
                  </Button>

                  {noRules && (
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled', fontStyle: 'italic' }}>
                      Enable at least one rule set with rules to fetch
                    </Typography>
                  )}
                  {lastFetchedAt && !loading && (
                    <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled', ml: 0.5 }}>
                      Last fetched {lastFetchedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                    </Typography>
                  )}
                </>
              );
            })()}

            {/* Grouped status toggle */}
            <Box
              onClick={() => setShowGrouped(v => !v)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.5,
                pl: 0.5, pr: 1.25, py: 0.25,
                borderRadius: '8px',
                border: '1px solid',
                borderColor: showGrouped ? 'rgba(16,185,129,0.35)' : 'rgba(148,163,184,0.22)',
                backgroundColor: showGrouped ? 'rgba(16,185,129,0.09)' : 'transparent',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                userSelect: 'none',
                '&:hover': {
                  borderColor: showGrouped ? 'rgba(16,185,129,0.55)' : 'rgba(148,163,184,0.4)',
                  backgroundColor: showGrouped ? 'rgba(16,185,129,0.13)' : 'rgba(148,163,184,0.06)',
                },
              }}
            >
              <Checkbox
                size="small"
                checked={showGrouped}
                onChange={e => setShowGrouped(e.target.checked)}
                onClick={e => e.stopPropagation()}
                sx={{
                  p: 0.25,
                  color: 'rgba(16,185,129,0.4)',
                  '&.Mui-checked': { color: '#34d399' },
                }}
              />
              <Typography sx={{
                fontSize: '0.75rem',
                fontWeight: showGrouped ? 600 : 400,
                color: showGrouped ? '#34d399' : 'text.secondary',
                lineHeight: 1,
                transition: 'color 0.15s ease',
              }}>
                Show Grouped Data
              </Typography>
              {lineItems.length > 0 && (() => {
                const groupedCount   = filteredItems.filter(i =>  i._is_grouped).length;
                const ungroupedCount = filteredItems.filter(i => !i._is_grouped).length;
                const hiddenCount    = showGrouped ? ungroupedCount : groupedCount;
                const shownCount     = showGrouped ? groupedCount   : ungroupedCount;
                return (
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '0.65rem',
                      color: showGrouped ? 'rgba(52,211,153,0.7)' : 'text.disabled',
                      ml: 0.25,
                      lineHeight: 1,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {shownCount}/{shownCount + hiddenCount}
                    {hiddenCount > 0 && (
                      <Typography
                        component="span"
                        sx={{
                          fontSize: '0.63rem',
                          color: 'warning.main',
                          ml: 0.4,
                          opacity: 0.85,
                        }}
                      >
                        ({hiddenCount} hidden)
                      </Typography>
                    )}
                  </Typography>
                );
              })()}
            </Box>

            {/* Show Excluded toggle — always visible after a fetch so client can audit hidden POs */}
            {filteredItems.length > 0 && (
              <Tooltip
                title={
                  excludedCount === 0
                    ? 'All fetched POs matched a rule set — nothing is hidden'
                    : showExcluded
                      ? `Hiding ${excludedCount} PO line item${excludedCount !== 1 ? 's' : ''} that didn't match any rule set`
                      : `${excludedCount} PO line item${excludedCount !== 1 ? 's' : ''} exist but are hidden because they didn't match any active rule set. Click to reveal them.`
                }
                arrow
              >
                <Box
                  onClick={() => excludedCount > 0 && setShowExcluded(v => !v)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 0.6,
                    pl: 0.75, pr: 1.25, py: 0.4, borderRadius: '8px',
                    border: '1px solid',
                    borderColor: excludedCount === 0
                      ? 'rgba(16,185,129,0.3)'
                      : showExcluded ? 'rgba(239,68,68,0.45)' : 'rgba(239,68,68,0.55)',
                    backgroundColor: excludedCount === 0
                      ? 'rgba(16,185,129,0.06)'
                      : showExcluded ? 'rgba(239,68,68,0.08)' : 'rgba(239,68,68,0.06)',
                    cursor: excludedCount > 0 ? 'pointer' : 'default',
                    transition: 'all 0.15s ease', userSelect: 'none',
                    '&:hover': excludedCount > 0 ? {
                      borderColor: 'rgba(239,68,68,0.7)',
                      backgroundColor: 'rgba(239,68,68,0.13)',
                    } : {},
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={showExcluded}
                    disabled={excludedCount === 0}
                    onChange={e => setShowExcluded(e.target.checked)}
                    onClick={e => e.stopPropagation()}
                    sx={{
                      p: 0.2,
                      color: excludedCount === 0 ? 'rgba(16,185,129,0.5)' : 'rgba(239,68,68,0.5)',
                      '&.Mui-checked': { color: '#f87171' },
                      '&.Mui-disabled': { color: 'rgba(16,185,129,0.4)' },
                    }}
                  />
                  <Box>
                    <Typography sx={{
                      fontSize: '0.73rem', fontWeight: 600, lineHeight: 1.2,
                      color: excludedCount === 0 ? '#10b981' : showExcluded ? '#f87171' : '#fca5a5',
                    }}>
                      {excludedCount === 0 ? 'All POs visible' : 'Show hidden POs'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.62rem', lineHeight: 1.1, color: 'text.disabled' }}>
                      {excludedCount === 0
                        ? 'no rule exclusions'
                        : `${excludedCount} excluded by rules`}
                    </Typography>
                  </Box>
                </Box>
              </Tooltip>
            )}

            {lineItems.length > 0 && (
              <>
                {/* Check All toggle */}
                {(() => {
                  const ungrouped = filteredItems.filter(i => !i._is_grouped);
                  const allChecked = ungrouped.length > 0 && ungrouped.every(i => tickedIds.has(i.id));
                  const someChecked = !allChecked && ungrouped.some(i => tickedIds.has(i.id));
                  const handleCheckAll = () => {
                    if (allChecked) {
                      setTickedIds(new Set());
                    } else {
                      openBatchMode();
                    }
                  };
                  return (
                    <Box
                      onClick={handleCheckAll}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 0.5,
                        pl: 0.5, pr: 1.25, py: 0.25, borderRadius: '8px',
                        border: '1px solid',
                        borderColor: allChecked ? 'rgba(99,102,241,0.35)' : someChecked ? 'rgba(99,102,241,0.25)' : 'rgba(148,163,184,0.22)',
                        backgroundColor: allChecked ? 'rgba(99,102,241,0.09)' : someChecked ? 'rgba(99,102,241,0.05)' : 'transparent',
                        cursor: ungrouped.length > 0 ? 'pointer' : 'default',
                        transition: 'all 0.15s ease', userSelect: 'none',
                        '&:hover': ungrouped.length > 0 ? { borderColor: 'rgba(99,102,241,0.5)', backgroundColor: 'rgba(99,102,241,0.06)' } : {},
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={allChecked}
                        indeterminate={someChecked}
                        onChange={handleCheckAll}
                        sx={{ p: 0.25, color: 'rgba(99,102,241,0.4)', '&.Mui-checked': { color: '#818cf8' }, '&.MuiCheckbox-indeterminate': { color: '#818cf8' } }}
                      />
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: allChecked || someChecked ? 600 : 400, color: allChecked || someChecked ? '#818cf8' : 'text.secondary', lineHeight: 1, transition: 'color 0.15s ease' }}>
                        Check All
                      </Typography>
                    </Box>
                  );
                })()}
                <Box sx={{ flex: 1 }} />
                <Button variant="outlined" size="small" startIcon={<VisibilityIcon sx={{ fontSize: 15 }} />}
                  onClick={() => navigate('/groups')} sx={{ height: 32 }}>
                  View Groups
                </Button>
              </>
            )}
          </Box>

          {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
        </Paper>

        {/* ── Data Table ── */}
        <Paper elevation={0} sx={{ display: 'flex', flexDirection: 'column', borderRadius: '12px', border: '1px solid', borderColor: 'divider', position: 'relative', width: '100%', minWidth: 0 }}>

          {/* ── Top horizontal scrollbar mirror ── */}
          {/* Synced with the real TableContainer so the user can scroll without
              scrolling to the bottom of a long table. */}
          <Box
            ref={topScrollRef}
            onScroll={e => {
              if (tableContainerRef.current)
                tableContainerRef.current.scrollLeft = e.currentTarget.scrollLeft;
            }}
            sx={{
              overflowX: 'auto', overflowY: 'hidden', height: 10,
              borderBottom: '1px solid', borderColor: 'divider',
              borderRadius: '12px 12px 0 0',
              // Always-visible thin scrollbar
              '&::-webkit-scrollbar': { height: 8 },
              '&::-webkit-scrollbar-track': { bgcolor: 'action.hover', borderRadius: 4 },
              '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(99,102,241,0.45)', borderRadius: 4, '&:hover': { bgcolor: 'rgba(99,102,241,0.7)' } },
            }}
          >
            {/* Invisible wide spacer — width kept in sync via useEffect below */}
            <Box id="top-scroll-spacer" sx={{ height: 1 }} />
          </Box>

          {/* ── Left / Right scroll arrow buttons ── */}
          <Box
            onClick={() => { if (tableContainerRef.current) tableContainerRef.current.scrollLeft -= 200; }}
            sx={{
              position: 'absolute', left: 6, top: '50%', transform: 'translateY(-50%)',
              zIndex: 10, cursor: 'pointer',
              width: 26, height: 26, borderRadius: '50%',
              bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
              '&:hover': { bgcolor: 'rgba(99,102,241,0.1)', borderColor: 'rgba(99,102,241,0.5)' },
              transition: 'all 0.15s',
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
          </Box>
          <Box
            onClick={() => { if (tableContainerRef.current) tableContainerRef.current.scrollLeft += 200; }}
            sx={{
              position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
              zIndex: 10, cursor: 'pointer',
              width: 26, height: 26, borderRadius: '50%',
              bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
              '&:hover': { bgcolor: 'rgba(99,102,241,0.1)', borderColor: 'rgba(99,102,241,0.5)' },
              transition: 'all 0.15s',
            }}
          >
            <ChevronRightIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
          </Box>

          <TableContainer
            ref={tableContainerRef}
            onScroll={e => {
              // Keep top scrollbar mirror in sync
              if (topScrollRef.current)
                topScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
            }}
            sx={{
              overflowX: 'auto',
              overflowY: 'auto',
              maxHeight: 'calc(100vh - 260px)',
              // Always-visible horizontal scrollbar
              '&::-webkit-scrollbar': { height: 8, width: 8 },
              '&::-webkit-scrollbar-track': { bgcolor: 'action.hover', borderRadius: 4 },
              '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(99,102,241,0.45)', borderRadius: 4, '&:hover': { bgcolor: 'rgba(99,102,241,0.7)' } },
              '&::-webkit-scrollbar-corner': { bgcolor: 'action.hover' },
            }}
          >
            <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', width: '100%', minWidth: selectedColumns.reduce((s, k) => s + (colWidths[k] || 100), 120 + 40), '& .MuiTableCell-root': { py: 1.5, px: 2, fontSize: '0.85rem' } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: 'background.paper', width: 82, p: '0 4px' }} />
                  {selectedColumns
                    .map(key => visibleFields.find(f => f.key === key))
                    .filter(Boolean)
                    .map(f => {
                      const label = showGrouped && f.key === '_suggested_group_id'   ? 'Group ID'
                                  : showGrouped && f.key === '_suggested_group_name' ? 'Group Attributes'
                                  : f.label;
                      const isDragOver = dragOverColKey === f.key && dragColKey !== f.key;
                      return (
                        <TableCell
                          key={f.key}
                          draggable
                          onDragStart={e => { e.dataTransfer.effectAllowed = 'move'; setDragColKey(f.key); }}
                          onDragOver={e => {
                            e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverColKey(f.key);
                            // Auto-scroll when dragging near left/right edges of the table container
                            const container = tableContainerRef.current;
                            if (container) {
                              const rect = container.getBoundingClientRect();
                              const ZONE = 80; // px from edge to trigger scroll
                              if (e.clientX < rect.left + ZONE) startAutoScroll(-1);
                              else if (e.clientX > rect.right - ZONE) startAutoScroll(1);
                              else stopAutoScroll();
                            }
                          }}
                          onDragLeave={() => { setDragOverColKey(null); stopAutoScroll(); }}
                          onDrop={e => { e.preventDefault(); handleColDrop(dragColKey, f.key); setDragColKey(null); setDragOverColKey(null); stopAutoScroll(); }}
                          onDragEnd={() => { setDragColKey(null); setDragOverColKey(null); stopAutoScroll(); }}
                          sx={{
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            bgcolor: 'background.paper',
                            cursor: 'grab',
                            userSelect: 'none',
                            transition: 'background-color 0.15s ease, color 0.15s ease, border-left 0.1s ease, opacity 0.1s ease',
                            borderLeft: isDragOver ? '3px solid #6366f1' : '3px solid transparent',
                            opacity: dragColKey === f.key ? 0.45 : 1,
                            minWidth: colWidths[f.key] || 100,
                            width: colWidths[f.key] || 100,
                            '&:hover': {
                              bgcolor: 'rgba(99,102,241,0.07)',
                              color: '#818cf8',
                              '& .drag-hint': { opacity: 1 },
                            },
                            '&:active': { cursor: 'grabbing' },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, overflow: 'hidden' }}>
                            <Box
                              className="drag-hint"
                              sx={{
                                display: 'flex', flexDirection: 'column', gap: '2px',
                                opacity: 0, transition: 'opacity 0.15s ease', flexShrink: 0,
                              }}
                            >
                              {[0,1,2].map(r => (
                                <Box key={r} sx={{ display: 'flex', gap: '2px' }}>
                                  {[0,1].map(c => (
                                    <Box key={c} sx={{ width: 2, height: 2, borderRadius: '50%', bgcolor: 'currentColor', opacity: 0.6 }} />
                                  ))}
                                </Box>
                              ))}
                            </Box>
                            <Tooltip title={label} arrow>
                              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                                {label}
                              </Box>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      );
                    })}
                  <TableCell align="right" sx={{
                    width: 40, p: 0,
                    position: 'sticky', right: 0, zIndex: 3,
                    bgcolor: 'background.paper',
                    borderLeft: '1px solid', borderColor: 'divider',
                  }}>
                    <IconButton size="small" onClick={e => setColMenuAnchor(e.currentTarget)}>
                      <ViewColumnIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayItems.map((item, idx) => {
                  const isTicked    = tickedIds.has(item.id);
                  const isCrossed   = crossedIds.has(item.id);
                  const isGrouped   = item._is_grouped;
                  const isExcluded  = !!item._excluded_by_rules;
                  const isExistingSuggestion = !isGrouped && item._suggested_group_id && !String(item._suggested_group_id).startsWith('NEW-');
                  const hasValueInChoice = !!item._value_in_choice; // new value → offer a choice
                  const isAmbiguous = !!item._ambiguous_group;    // multiple existing groups match → user must pick

                  const subVendorTag = SUB_VENDOR_TAG[item.vendor] || (
                    PARENT_VENDOR_LABEL[item.vendor]
                      ? { label: item.vendor.slice(0, 2).toUpperCase(), full: PARENT_VENDOR_LABEL[item.vendor], color: '#6366f1' }
                      : null
                  );

                  const openConfirm = () => {
                    const group = item._group_object
                      || fetchedGroups.find(g => String(g.id) === String(item._suggested_group_id));
                    if (group) {
                      // Pre-fill Bernardo Style # from the existing group so the image
                      // loads automatically without the user having to type it.
                      const prefill = (group.bernardo_style_number || '').trim();
                      setDialogImgStyle(prefill);
                      setDialogImgSearched(prefill);
                      setDialogImgModal(false);
                      setGroupConfirmDialog({ group, item, mode: isGrouped ? 'grouped' : 'existing_suggestion' });
                    }
                  };

                  // For items with a new value_in field value, show a choice dialog
                  // instead of silently routing to the existing group.
                  const openValueInChoice = () => {
                    const group = fetchedGroups.find(g => String(g.id) === String(item._suggested_group_id));
                    if (group) setValueInChoiceDialog({ item, group });
                    else openConfirm(); // fallback
                  };

                  // For ambiguous items (multiple existing groups share the same rule key),
                  // show a picker dialog listing all matching groups with their items.
                  const openAmbiguousDialog = () => {
                    setAmbiguousGroupDialog({ item, matchingGroups: item._matching_groups || [] });
                  };

                  return (
                  <TableRow key={idx} hover
                    sx={{
                      bgcolor: isGrouped
                        ? 'rgba(16,185,129,0.07)'
                        : isExcluded
                          ? 'rgba(239,68,68,0.03)'
                          : isTicked
                            ? 'rgba(16,185,129,0.08)'
                            : isCrossed
                              ? 'rgba(148,163,184,0.04)'
                              : 'inherit',
                      opacity: isCrossed ? 0.42 : isExcluded ? 0.55 : 1,
                      transition: 'all 0.15s ease',
                      borderLeft: isGrouped ? '3px solid #34d399' : isExcluded ? '3px solid rgba(239,68,68,0.4)' : '3px solid transparent',
                    }}>

                    {/* ── Action cell ── */}
                    <TableCell sx={{ p: '0 4px', width: isExcluded ? 36 : isExistingSuggestion ? 82 : 52 }} onClick={e => e.stopPropagation()}>
                      {isExcluded ? (
                        /* Excluded by rules — show a muted icon, no group actions */
                        <Tooltip title="This item doesn't match any active rule set (e.g. its Ship To value is not in the permitted list)" placement="top" arrow>
                          <Box sx={{ width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(239,68,68,0.45)', fontSize: '0.9rem' }}>
                            ⊘
                          </Box>
                        </Tooltip>
                      ) : isGrouped ? (
                        /* Already in a group — green checkmark, click to view group */
                        <Tooltip title="View group details" placement="top">
                          <IconButton size="small" onClick={openConfirm}
                            sx={{
                              width: 26, height: 26,
                              bgcolor: 'rgba(16,185,129,0.15)', color: '#34d399',
                              border: '1px solid rgba(16,185,129,0.4)', borderRadius: '6px',
                              '&:hover': { bgcolor: 'rgba(16,185,129,0.28)' },
                            }}>
                            <CheckIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Box sx={{ display: 'flex', gap: 0.25 }}>
                          <Tooltip title={isTicked ? 'Remove from group' : isAmbiguous ? 'Multiple groups match — pick one' : 'Add to group'} placement="top">
                            <IconButton size="small"
                              onClick={() => {
                                if (isTicked) {
                                  // Already staged — untick
                                  setTickedIds(prev => { const n = new Set(prev); n.delete(item.id); return n; });
                                } else if (isAmbiguous) {
                                  // Multiple existing groups match — user must pick
                                  openAmbiguousDialog();
                                } else if (hasValueInChoice) {
                                  // New value_in value — ask the user first
                                  openValueInChoice();
                                } else {
                                  // Open the batch dialog for this item's suggested group
                                  if (item._suggested_group_id) {
                                    openBatchMode(item._suggested_group_id);
                                  } else {
                                    // No suggestion yet — stage it and open all ungrouped
                                    setTickedIds(prev => { const n = new Set(prev); n.add(item.id); return n; });
                                    setCrossedIds(prev => { const n = new Set(prev); n.delete(item.id); return n; });
                                    openBatchMode(null);
                                  }
                                }
                              }}
                              sx={{
                                width: 26, height: 26,
                                bgcolor: isTicked ? 'rgba(16,185,129,0.15)' : 'transparent',
                                color: isTicked ? '#34d399' : 'rgba(148,163,184,0.5)',
                                border: '1px solid',
                                borderColor: isTicked ? 'rgba(16,185,129,0.4)' : 'rgba(148,163,184,0.2)',
                                borderRadius: '6px',
                                '&:hover': { bgcolor: 'rgba(16,185,129,0.2)', color: '#34d399', borderColor: 'rgba(16,185,129,0.5)' },
                              }}>
                              <CheckIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={isCrossed ? 'Restore item' : 'Exclude from group'} placement="top">
                            <IconButton size="small"
                              onClick={() => {
                                setCrossedIds(prev => { const n = new Set(prev); isCrossed ? n.delete(item.id) : n.add(item.id); return n; });
                                if (!isCrossed) setTickedIds(prev => { const n = new Set(prev); n.delete(item.id); return n; });
                              }}
                              sx={{
                                width: 26, height: 26,
                                bgcolor: isCrossed ? 'rgba(239,68,68,0.12)' : 'transparent',
                                color: isCrossed ? '#f87171' : 'rgba(148,163,184,0.5)',
                                border: '1px solid',
                                borderColor: isCrossed ? 'rgba(239,68,68,0.35)' : 'rgba(148,163,184,0.2)',
                                borderRadius: '6px',
                                '&:hover': { bgcolor: 'rgba(239,68,68,0.18)', color: '#f87171', borderColor: 'rgba(239,68,68,0.45)' },
                              }}>
                              <CloseIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                          {/* Add-to-group button — always visible for ungrouped items:
                              ambiguous (multiple existing groups) → picker dialog (red-orange);
                              new value_in value → choice dialog (amber);
                              existing suggestion (known value) → group confirm dialog (green);
                              NEW / no suggestion → stage item (indigo) */}
                          {(
                            <Tooltip
                              title={
                                isAmbiguous        ? 'Multiple groups match — pick one' :
                                hasValueInChoice   ? 'New value — choose: add to existing or new group' :
                                isExistingSuggestion ? 'Add to existing group' :
                                                     'Stage for group creation'
                              }
                              placement="top"
                            >
                              <IconButton size="small"
                                onClick={() => {
                                  if (isAmbiguous) {
                                    openAmbiguousDialog();
                                  } else if (hasValueInChoice) {
                                    openValueInChoice();
                                  } else if (isExistingSuggestion) {
                                    openConfirm();
                                  } else {
                                    setTickedIds(prev => { const n = new Set(prev); n.add(item.id); return n; });
                                    setCrossedIds(prev => { const n = new Set(prev); n.delete(item.id); return n; });
                                  }
                                }}
                                sx={{
                                  width: 26, height: 26,
                                  bgcolor: isAmbiguous      ? 'rgba(168,85,247,0.1)'   : hasValueInChoice ? 'rgba(245,158,11,0.1)' : isExistingSuggestion ? 'rgba(16,185,129,0.1)' : 'rgba(99,102,241,0.1)',
                                  color:   isAmbiguous      ? '#a855f7'                 : hasValueInChoice ? '#f59e0b'              : isExistingSuggestion ? '#34d399'              : '#818cf8',
                                  border: '1px solid',
                                  borderColor: isAmbiguous  ? 'rgba(168,85,247,0.3)'   : hasValueInChoice ? 'rgba(245,158,11,0.3)' : isExistingSuggestion ? 'rgba(16,185,129,0.3)' : 'rgba(99,102,241,0.3)',
                                  borderRadius: '6px',
                                  '&:hover': {
                                    bgcolor:     isAmbiguous ? 'rgba(168,85,247,0.2)'   : hasValueInChoice ? 'rgba(245,158,11,0.22)' : isExistingSuggestion ? 'rgba(16,185,129,0.22)' : 'rgba(99,102,241,0.2)',
                                    borderColor: isAmbiguous ? 'rgba(168,85,247,0.55)'  : hasValueInChoice ? 'rgba(245,158,11,0.55)' : isExistingSuggestion ? 'rgba(16,185,129,0.55)' : 'rgba(99,102,241,0.55)',
                                  },
                                }}>
                                <GroupWorkIcon sx={{ fontSize: 13 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      )}
                    </TableCell>

                    {selectedColumns.map(key => visibleFields.find(f => f.key === key)).filter(Boolean).map(f => (
                      <TableCell key={f.key} sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        minWidth: colWidths[f.key] || 100,
                        width: colWidths[f.key] || 100,
                        ...(f.key === '_suggested_group_name' && { maxWidth: 340 }),
                      }}>
                        {f.key === '_suggested_group_id' ? (
                          isExcluded ? (
                            <Tooltip title="Doesn't match any active rule set — not eligible for grouping" placement="top" arrow>
                              <Chip
                                size="small"
                                label="No rule match"
                                sx={{
                                  fontSize: '0.63rem', fontWeight: 600, height: 18,
                                  bgcolor: 'rgba(239,68,68,0.08)', color: 'rgba(239,68,68,0.6)',
                                  border: '1px solid rgba(239,68,68,0.2)', cursor: 'default',
                                }}
                              />
                            </Tooltip>
                          ) : isAmbiguous ? (
                            <Tooltip title={`${(item._matching_groups || []).length} existing groups match — click the group icon to pick one`} placement="top" arrow>
                              <Chip
                                size="small"
                                clickable
                                onClick={e => { e.stopPropagation(); openAmbiguousDialog(); }}
                                label={`${(item._matching_groups || []).length} groups`}
                                sx={{
                                  fontSize: '0.63rem', fontWeight: 700, height: 20,
                                  bgcolor: 'rgba(168,85,247,0.1)', color: '#a855f7',
                                  border: '1px solid rgba(168,85,247,0.3)', cursor: 'pointer',
                                }}
                              />
                            </Tooltip>
                          ) : item._suggested_group_id ? (
                            <Chip
                              size="small"
                              clickable
                              onClick={e => {
                                e.stopPropagation();
                                if (isExistingSuggestion) openConfirm();
                                else openBatchMode(item._suggested_group_id);
                              }}
                              label={String(item._suggested_group_id).startsWith('NEW-') ? String(item._suggested_group_id).replace('NEW-', '') : `ID: ${item._suggested_group_id}`}
                              sx={{
                                fontSize: '0.7rem', fontWeight: 700, height: 20,
                                bgcolor: String(item._suggested_group_id).startsWith('NEW-')
                                  ? 'rgba(99,102,241,0.12)' : 'rgba(16,185,129,0.12)',
                                color: String(item._suggested_group_id).startsWith('NEW-')
                                  ? '#818cf8' : '#34d399',
                                border: '1px solid',
                                borderColor: String(item._suggested_group_id).startsWith('NEW-')
                                  ? 'rgba(99,102,241,0.3)' : 'rgba(16,185,129,0.3)',
                                cursor: 'pointer',
                              }}
                            />
                          ) : '—'
                        ) : f.key === '_suggested_group_name' ? (
                          isExcluded ? (
                            <Typography sx={{ fontSize: '0.75rem', color: 'rgba(239,68,68,0.45)', fontStyle: 'italic' }}>—</Typography>
                          ) : isAmbiguous ? (
                            <Tooltip title="Multiple existing groups match this item — click the group icon to resolve" placement="top" arrow enterDelay={200}>
                              <Typography noWrap sx={{ fontSize: '0.78rem', color: '#a855f7', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 320 }}>
                                Conflict: {(item._matching_groups || []).map(g => g.name).join(' · ')}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Tooltip title={item._suggested_group_name || ''} placement="top" arrow enterDelay={400}>
                              <Typography noWrap sx={{ fontSize: '0.8rem', color: 'text.secondary', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 320 }}>
                                {item._suggested_group_name || '—'}
                              </Typography>
                            </Tooltip>
                          )
                        ) : f.key === 'customer_po_number' && subVendorTag ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                            <span>{item[f.key] || '—'}</span>
                            <Box sx={{
                              display: 'inline-flex', alignSelf: 'flex-start',
                              px: 0.7, py: 0.15, borderRadius: '4px',
                              fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.02em',
                              whiteSpace: 'nowrap', lineHeight: 1.6,
                              bgcolor: `${subVendorTag.color}18`,
                              color: subVendorTag.color,
                              border: `1px solid ${subVendorTag.color}40`,
                              cursor: 'default',
                            }}>
                              {subVendorTag.full}
                            </Box>
                          </Box>
                        ) : (
                          item[f.key] || '—'
                        )}
                      </TableCell>
                    ))}
                    <TableCell sx={{
                      position: 'sticky', right: 0, zIndex: 1,
                      bgcolor: 'background.paper',
                      borderLeft: '1px solid', borderColor: 'divider',
                      width: 40, p: 0,
                    }} />
                  </TableRow>
                  );
                })}
                {!displayItems.length && !loading && (
                  <TableRow>
                    <TableCell colSpan={selectedColumns.length + 3} align="center" sx={{ py: 6, fontSize: '0.8rem !important' }}>
                      {lineItems.length === 0
                        ? 'No line items to display. Fetch data above to get started.'
                        : showGrouped
                          ? (() => {
                              const ungroupedCount = filteredItems.filter(i => !i._is_grouped).length;
                              return (
                                <Box>
                                  <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary', mb: 0.5 }}>No grouped items found for this date range.</Typography>
                                  {ungroupedCount > 0 && (
                                    <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>
                                      {ungroupedCount} ungrouped item{ungroupedCount !== 1 ? 's' : ''} available — uncheck <strong>Show Grouped Data</strong> to view them.
                                    </Typography>
                                  )}
                                </Box>
                              );
                            })()
                          : (() => {
                              const groupedCount = filteredItems.filter(i => i._is_grouped).length;
                              return (
                                <Box>
                                  <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary', mb: 0.5 }}>No ungrouped items found for this date range.</Typography>
                                  {groupedCount > 0 && (
                                    <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>
                                      {groupedCount} grouped item{groupedCount !== 1 ? 's' : ''} exist — check <strong>Show Grouped Data</strong> to view them.
                                    </Typography>
                                  )}
                                </Box>
                              );
                            })()
                      }
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        </Box>{/* end main content */}

        {/* ── Value-In Choice Dialog ───────────────────────────────────────────
             Shown when a fetched item has a value_in field value that is NOT yet
             present in the existing suggested group (e.g. ship_to = US when the
             group was created with Canada + Mexico).  User picks:
               A) Add to existing group  →  opens the normal group-confirm dialog
               B) Create a new group     →  stages the item for a new group
        ──────────────────────────────────────────────────────────────────────── */}
        {valueInChoiceDialog && (() => {
          const { item: choiceItem, group: choiceGroup } = valueInChoiceDialog;
          const previewItems = Array.isArray(choiceGroup.group_data) ? choiceGroup.group_data.slice(0, 5) : [];
          const previewCols = ['customer_style_number', 'color', 'size', 'ship_to'].filter(col =>
            previewItems.some(i => i[col] != null && String(i[col]).trim())
          );
          return (
            <Dialog
              open={!!valueInChoiceDialog}
              onClose={() => setValueInChoiceDialog(null)}
              maxWidth="sm"
              fullWidth
              PaperProps={{ sx: { borderRadius: '14px' } }}
            >
              <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{
                    width: 30, height: 30, borderRadius: '8px',
                    background: 'linear-gradient(135deg, rgba(245,158,11,0.2), rgba(245,158,11,0.08))',
                    border: '1px solid rgba(245,158,11,0.35)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <GroupWorkIcon sx={{ fontSize: 15, color: '#f59e0b' }} />
                  </Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.2 }}>
                    New Value Detected
                  </Typography>
                </Box>
                <IconButton size="small" onClick={() => setValueInChoiceDialog(null)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </DialogTitle>

              <DialogContent sx={{ pt: 0.5, pb: 1 }}>
                <Alert severity="warning" sx={{ mb: 2, fontSize: '0.78rem' }}>
                  This item's value is <strong>not yet present</strong> in the existing group. Choose how to proceed.
                </Alert>

                {/* Existing group info + item preview */}
                <Box sx={{ mb: 1.5, p: 1.25, borderRadius: '8px', border: '1px solid', borderColor: 'divider', bgcolor: 'rgba(0,0,0,0.02)' }}>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.disabled', mb: 0.5 }}>
                    Existing Group
                  </Typography>
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>{choiceGroup.group_name}</Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mb: previewItems.length ? 1 : 0 }}>
                    ID: {choiceGroup.id} · {Array.isArray(choiceGroup.line_item_ids) ? choiceGroup.line_item_ids.length : 0} item{Array.isArray(choiceGroup.line_item_ids) && choiceGroup.line_item_ids.length !== 1 ? 's' : ''}
                  </Typography>
                  {previewItems.length > 0 && previewCols.length > 0 && (
                    <Box sx={{ overflowX: 'auto' }}>
                      <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.4, px: 0.75, fontSize: '0.72rem', whiteSpace: 'nowrap' } }}>
                        <TableHead>
                          <TableRow>
                            {previewCols.map(col => (
                              <TableCell key={col} sx={{ fontWeight: 700, color: 'text.secondary', bgcolor: 'transparent', borderBottom: '1px solid', borderColor: 'divider' }}>
                                {FIELDS.find(f => f.key === col)?.label || col}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {previewItems.map((pi, i) => (
                            <TableRow key={i}>
                              {previewCols.map(col => (
                                <TableCell key={col} sx={{ color: 'text.primary' }}>{pi[col] || '—'}</TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      {Array.isArray(choiceGroup.group_data) && choiceGroup.group_data.length > 5 && (
                        <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled', mt: 0.5, pl: 0.5 }}>
                          +{choiceGroup.group_data.length - 5} more items in this group
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </DialogContent>

              <DialogActions sx={{ px: 2.5, pb: 2.5, gap: 1, flexDirection: 'column', alignItems: 'stretch' }}>
                {/* Option A — add to existing */}
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => {
                    setValueInChoiceDialog(null);
                    const prefill = (choiceGroup.bernardo_style_number || '').trim();
                    setDialogImgStyle(prefill);
                    setDialogImgSearched(prefill);
                    setDialogImgModal(false);
                    setGroupConfirmDialog({ group: choiceGroup, item: choiceItem, mode: 'existing_suggestion' });
                  }}
                  sx={{
                    background: 'linear-gradient(135deg, #10b981, #34d399)',
                    '&:hover': { background: 'linear-gradient(135deg, #059669, #10b981)' },
                    fontWeight: 600, fontSize: '0.82rem',
                  }}
                >
                  Add to existing group "{choiceGroup.group_name}"
                </Button>

                {/* Option B — new group */}
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => {
                    setValueInChoiceDialog(null);
                    setLineItems(prev => prev.map(i =>
                      i.id === choiceItem.id
                        ? { ...i, _suggested_group_id: `NEW-CHOICE-${i.id}`, _suggested_group_name: '', _value_in_choice: false }
                        : i
                    ));
                    setFilteredItems(prev => prev.map(i =>
                      i.id === choiceItem.id
                        ? { ...i, _suggested_group_id: `NEW-CHOICE-${i.id}`, _suggested_group_name: '', _value_in_choice: false }
                        : i
                    ));
                    setTickedIds(prev => { const n = new Set(prev); n.add(choiceItem.id); return n; });
                    setCrossedIds(prev => { const n = new Set(prev); n.delete(choiceItem.id); return n; });
                    setSnackbar({ open: true, msg: 'Item staged for a new group. Click "Check All" or the ✓ icon to review.', severity: 'info' });
                  }}
                  sx={{
                    borderColor: 'rgba(99,102,241,0.4)', color: '#818cf8',
                    fontWeight: 600, fontSize: '0.82rem',
                    '&:hover': { borderColor: '#818cf8', bgcolor: 'rgba(99,102,241,0.06)' },
                  }}
                >
                  Create a new group for this item
                </Button>
              </DialogActions>
            </Dialog>
          );
        })()}

        {/* ── Ambiguous Group Picker Dialog ────────────────────────────────────
             Shown when multiple existing groups share the same rule key (e.g.
             only a value_in rule is active with no match_all to differentiate).
             User sees all matching groups — each with a mini item preview —
             and picks which one to add the item to, or creates a new group.
        ──────────────────────────────────────────────────────────────────────── */}
        {ambiguousGroupDialog && (() => {
          const { item: ambigItem, matchingGroups } = ambiguousGroupDialog;
          const stageAsNew = () => {
            setAmbiguousGroupDialog(null);
            setLineItems(prev => prev.map(i =>
              i.id === ambigItem.id
                ? { ...i, _suggested_group_id: `NEW-CHOICE-${i.id}`, _suggested_group_name: '', _ambiguous_group: false, _matching_groups: [] }
                : i
            ));
            setFilteredItems(prev => prev.map(i =>
              i.id === ambigItem.id
                ? { ...i, _suggested_group_id: `NEW-CHOICE-${i.id}`, _suggested_group_name: '', _ambiguous_group: false, _matching_groups: [] }
                : i
            ));
            setTickedIds(prev => { const n = new Set(prev); n.add(ambigItem.id); return n; });
            setCrossedIds(prev => { const n = new Set(prev); n.delete(ambigItem.id); return n; });
            setSnackbar({ open: true, msg: 'Item staged for a new group.', severity: 'info' });
          };

          return (
            <Dialog
              open={!!ambiguousGroupDialog}
              onClose={() => setAmbiguousGroupDialog(null)}
              maxWidth="md"
              fullWidth
              PaperProps={{ sx: { borderRadius: '14px' } }}
            >
              <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{
                    width: 30, height: 30, borderRadius: '8px',
                    background: 'linear-gradient(135deg, rgba(168,85,247,0.2), rgba(168,85,247,0.08))',
                    border: '1px solid rgba(168,85,247,0.35)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <GroupWorkIcon sx={{ fontSize: 15, color: '#a855f7' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.2 }}>
                      Multiple Groups Match
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mt: 0.2 }}>
                      {matchingGroups.length} existing groups share the same rule key — pick one or create new
                    </Typography>
                  </Box>
                </Box>
                <IconButton size="small" onClick={() => setAmbiguousGroupDialog(null)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </DialogTitle>

              <DialogContent sx={{ pt: 0.5 }}>
                <Alert severity="error" sx={{ mb: 2, fontSize: '0.78rem' }}>
                  The active rules don't provide enough detail to automatically assign this item. Review the groups below and choose where it belongs.
                </Alert>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {matchingGroups.map(mg => {
                    const fullGroup = fetchedGroups.find(g => String(g.id) === String(mg.id)) || mg;
                    const previewItems = Array.isArray(fullGroup.group_data) ? fullGroup.group_data.slice(0, 4) : [];
                    const previewCols = ['customer_style_number', 'color', 'size', 'ship_to'].filter(col =>
                      previewItems.some(i => i[col] != null && String(i[col]).trim())
                    );
                    return (
                      <Box
                        key={mg.id}
                        sx={{
                          border: '1px solid', borderColor: 'divider', borderRadius: '10px',
                          p: 1.5, bgcolor: 'rgba(0,0,0,0.01)',
                        }}
                      >
                        {/* Group header */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: previewItems.length ? 1 : 0 }}>
                          <Box>
                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }}>{mg.name}</Typography>
                            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                              ID: {mg.id} · {Array.isArray(fullGroup.line_item_ids) ? fullGroup.line_item_ids.length : 0} item{Array.isArray(fullGroup.line_item_ids) && fullGroup.line_item_ids.length !== 1 ? 's' : ''}
                              {fullGroup.bernardo_style_number ? ` · Style: ${fullGroup.bernardo_style_number}` : ''}
                              {fullGroup.customer_item ? ` · Cust Item: ${fullGroup.customer_item}` : ''}
                            </Typography>
                          </Box>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => {
                              setAmbiguousGroupDialog(null);
                              // Resolve: point this item at the chosen group
                              setLineItems(prev => prev.map(i =>
                                i.id === ambigItem.id
                                  ? { ...i, _suggested_group_id: mg.id, _suggested_group_name: mg.name, _ambiguous_group: false, _matching_groups: [] }
                                  : i
                              ));
                              setFilteredItems(prev => prev.map(i =>
                                i.id === ambigItem.id
                                  ? { ...i, _suggested_group_id: mg.id, _suggested_group_name: mg.name, _ambiguous_group: false, _matching_groups: [] }
                                  : i
                              ));
                              // Now open the confirm dialog for the chosen group
                              const prefill = (fullGroup.bernardo_style_number || '').trim();
                              setDialogImgStyle(prefill);
                              setDialogImgSearched(prefill);
                              setDialogImgModal(false);
                              setGroupConfirmDialog({ group: fullGroup, item: ambigItem, mode: 'existing_suggestion' });
                            }}
                            sx={{
                              height: 28, fontSize: '0.75rem', fontWeight: 600, px: 1.5,
                              background: 'linear-gradient(135deg, #10b981, #34d399)',
                              '&:hover': { background: 'linear-gradient(135deg, #059669, #10b981)' },
                              flexShrink: 0,
                            }}
                          >
                            Add to this group
                          </Button>
                        </Box>

                        {/* Mini item preview table */}
                        {previewItems.length > 0 && previewCols.length > 0 && (
                          <Box sx={{ overflowX: 'auto', mt: 0.5 }}>
                            <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.3, px: 0.75, fontSize: '0.7rem', whiteSpace: 'nowrap' } }}>
                              <TableHead>
                                <TableRow>
                                  {previewCols.map(col => (
                                    <TableCell key={col} sx={{ fontWeight: 700, color: 'text.secondary', bgcolor: 'transparent', borderBottom: '1px solid', borderColor: 'divider' }}>
                                      {FIELDS.find(f => f.key === col)?.label || col}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {previewItems.map((pi, i) => (
                                  <TableRow key={i}>
                                    {previewCols.map(col => (
                                      <TableCell key={col}>{pi[col] || '—'}</TableCell>
                                    ))}
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                            {Array.isArray(fullGroup.group_data) && fullGroup.group_data.length > 4 && (
                              <Typography sx={{ fontSize: '0.67rem', color: 'text.disabled', mt: 0.4, pl: 0.5 }}>
                                +{fullGroup.group_data.length - 4} more items
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </DialogContent>

              <DialogActions sx={{ px: 2.5, pb: 2.5 }}>
                <Button
                  variant="outlined"
                  onClick={stageAsNew}
                  sx={{
                    borderColor: 'rgba(99,102,241,0.4)', color: '#818cf8',
                    fontWeight: 600, fontSize: '0.82rem',
                    '&:hover': { borderColor: '#818cf8', bgcolor: 'rgba(99,102,241,0.06)' },
                  }}
                >
                  Create a new group instead
                </Button>
                <Button onClick={() => setAmbiguousGroupDialog(null)} sx={{ color: 'text.secondary' }}>
                  Cancel
                </Button>
              </DialogActions>
            </Dialog>
          );
        })()}

        {/* Group Items Dialog */}
        <Dialog open={groupDialogOpen} onClose={() => setGroupDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><GroupWorkIcon color="secondary" /> Create Group ({selectedCount} items)</Box>
            <IconButton onClick={() => setGroupDialogOpen(false)} size="small"><CloseIcon /></IconButton>
          </DialogTitle>
          <DialogContent>
            {groupViolations.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Alert 
                  severity="error" 
                  sx={{ mb: 1, cursor: 'pointer', alignItems: 'center', '& .MuiAlert-message': { flex: 1 } }} 
                  onClick={() => setShowGroupViolations(!showGroupViolations)}
                  action={showGroupViolations ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                >
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold">Grouping Rule Violations</Typography>
                    <Typography variant="caption" display="block">The selected items violate the following rules. You cannot proceed until the selection is corrected.</Typography>
                  </Box>
                </Alert>
                <Collapse in={showGroupViolations}>
                  {groupViolations.map((v, i) => (
                    <Alert key={i} severity="warning" sx={{ mb: 0.5, py: 0.25 }}><Typography variant="caption">{v}</Typography></Alert>
                  ))}
                </Collapse>
              </Box>
            )}
            {groupViolations.length === 0 && (
              <Alert severity="success" sx={{ mb: 2 }}>All grouping rules are satisfied ✓</Alert>
            )}
            <TextField fullWidth label="Group Name / Key" value={groupName} onChange={e => setGroupName(e.target.value)}
              placeholder="e.g. Spring 2026 — Style 12345 — Red" autoFocus
              sx={{ mb: 2 }} />
            <TextField
              fullWidth
              label="Bernardo Style # *"
              value={bernadoStyleForGroup}
              onChange={e => setBernadoStyleForGroup(e.target.value)}
              placeholder="Required — enter Bernardo style number"
              onKeyDown={e => { if (e.key === 'Enter' && groupName.trim() && bernadoStyleForGroup.trim()) handleCreateGroup(); }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button onClick={() => setGroupDialogOpen(false)} disabled={grouping}>Cancel</Button>
            <Button variant="contained" color="secondary" onClick={handleCreateGroup}
              disabled={!groupName.trim() || grouping || groupViolations.length > 0} startIcon={grouping ? <CircularProgress size={16} /> : <GroupWorkIcon />}>
              {grouping ? 'Creating…' : 'Create Group'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Batch Group Review Dialog ── */}
        {batchOpen && batchQueue[batchIndex] && (() => {
          const current = batchQueue[batchIndex];
          const isNew = !current.gid || String(current.gid).startsWith('NEW-') || String(current.gid).startsWith('__solo_');
          const remainingCount = batchQueue.filter(entry => !confirmedGids.has(String(entry.gid))).length;
          // Nearest unconfirmed index before / after current position
          const prevUnconfirmedIdx = (() => {
            for (let i = batchIndex - 1; i >= 0; i--) {
              if (!confirmedGids.has(String(batchQueue[i].gid))) return i;
            }
            return -1;
          })();
          const nextUnconfirmedIdx = (() => {
            for (let i = batchIndex + 1; i < batchQueue.length; i++) {
              if (!confirmedGids.has(String(batchQueue[i].gid))) return i;
            }
            return -1;
          })();
          return (
            <Dialog
              open={batchOpen}
              onClose={() => !batchConfirming && setBatchOpen(false)}
              maxWidth="sm"
              fullWidth
              PaperProps={{ sx: { borderRadius: '14px' } }}
            >
              <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{
                    width: 32, height: 32, borderRadius: '8px',
                    background: 'linear-gradient(135deg, rgba(99,102,241,0.2), rgba(99,102,241,0.08))',
                    border: '1px solid rgba(99,102,241,0.35)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <GroupWorkIcon sx={{ fontSize: 16, color: '#818cf8' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem', fontFamily: '"DM Sans", sans-serif', lineHeight: 1.2 }}>
                      {isNew ? `Group ${String(current.gid).replace('NEW-', '')}` : `Group ID: ${current.gid}`}
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mt: 0.25 }}>
                      {current.items.filter(i => !batchExcludedIds.has(i.id)).length} of {current.items.length} item{current.items.length !== 1 ? 's' : ''} selected · {remainingCount} of {batchQueue.length} remaining
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {/* ← Prev arrow */}
                  <Tooltip title="Previous group" placement="top">
                    <span>
                      <IconButton size="small" onClick={() => navigateToBatch(prevUnconfirmedIdx)}
                        disabled={batchConfirming || prevUnconfirmedIdx === -1}
                        sx={{ color: 'text.secondary' }}>
                        <ChevronLeftIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>

                  {/* Clickable progress dots */}
                  <Box sx={{ display: 'flex', gap: 0.4, alignItems: 'center' }}>
                    {batchQueue.map((entry, i) => {
                      const label = String(entry.gid).startsWith('NEW-') || String(entry.gid).startsWith('__solo_')
                        ? String(entry.gid).replace('NEW-', '') : `ID: ${entry.gid}`;
                      const isDone = confirmedGids.has(String(entry.gid));
                      const isCurrent = i === batchIndex;
                      const isClickable = !batchConfirming && !isDone && !isCurrent;
                      return (
                        <Tooltip key={i} title={`${label} · ${entry.items.length} item${entry.items.length !== 1 ? 's' : ''}${isDone ? ' · ✓ confirmed' : ''}`} placement="bottom">
                          <Box
                            onClick={() => isClickable && navigateToBatch(i)}
                            sx={{
                              width: isCurrent ? 20 : 8, height: 8, borderRadius: 4,
                              bgcolor: isDone ? '#34d399' : isCurrent ? '#818cf8' : 'rgba(148,163,184,0.3)',
                              transition: 'all 0.2s ease',
                              cursor: isClickable ? 'pointer' : 'default',
                              '&:hover': isClickable ? {
                                bgcolor: isCurrent ? '#6366f1' : 'rgba(99,102,241,0.5)',
                                transform: 'scaleY(1.4)',
                              } : {},
                            }}
                          />
                        </Tooltip>
                      );
                    })}
                  </Box>

                  {/* → Next arrow */}
                  <Tooltip title="Next group" placement="top">
                    <span>
                      <IconButton size="small" onClick={() => navigateToBatch(nextUnconfirmedIdx)}
                        disabled={batchConfirming || nextUnconfirmedIdx === -1}
                        sx={{ color: 'text.secondary' }}>
                        <ChevronRightIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>

                  <IconButton onClick={() => setBatchOpen(false)} size="small" disabled={batchConfirming}
                    sx={{ ml: 0.5 }}>
                    <CloseIcon />
                  </IconButton>
                </Box>
              </DialogTitle>

              <DialogContent dividers sx={{ p: 0 }}>
                {/* Group name + Bernardo Style # fields */}
                <Box sx={{ px: 2.5, pt: 2, pb: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <TextField
                    fullWidth size="small"
                    label={isNew ? 'Group Name' : 'Group Name (existing)'}
                    value={batchGroupName}
                    onChange={e => {
                      const val = e.target.value;
                      setBatchGroupName(val);
                      setBatchQueue(prev => prev.map((entry, i) => i === batchIndex ? { ...entry, name: val } : entry));
                    }}
                    disabled={!isNew}
                    sx={{ mb: 0 }}
                  />
                  <TextField
                    fullWidth size="small"
                    label="Bernardo Style #"
                    placeholder="Optional — enter Bernardo style number"
                    value={batchBernadoStyle}
                    onChange={e => {
                      const val = e.target.value;
                      setBatchBernadoStyle(val);
                      setBatchStyleIsSuggested(false); // user is manually editing — no longer a suggestion
                      setBatchQueue(prev => prev.map((entry, i) => i === batchIndex ? { ...entry, bernadoStyle: val, bernadoStyleIsSuggested: false } : entry));
                      // Loading state + debounced fetch handled centrally via useEffect
                    }}
                    onPaste={e => {
                      e.preventDefault();
                      const pasted = e.clipboardData.getData('text');
                      // Strip newlines, tabs, non-breaking spaces, zero-width chars that spreadsheets inject
                      const cleaned = pasted.replace(/[\n\r\t ​‌‍﻿]/g, '').trim();
                      setBatchBernadoStyle(cleaned);
                      setBatchStyleIsSuggested(false);
                      setBatchQueue(prev => prev.map((entry, i) => i === batchIndex ? { ...entry, bernadoStyle: cleaned, bernadoStyleIsSuggested: false } : entry));
                      // Loading state + debounced fetch handled centrally via useEffect
                    }}
                    onKeyDown={e => { if (e.key === 'Enter' && !batchConfirming) handleBatchConfirm(); }}
                    disabled={batchConfirming}
                    autoFocus
                    sx={{ mb: 0 }}
                  />

                  {/* ── Bernardo Style # suggestions (from group + from style map) ── */}
                  {(() => {
                    const fromGroup = batchQueue[batchIndex]?.bernadoStyleFromGroup || '';
                    const fromMap   = batchQueue[batchIndex]?.bernadoStyleFromMap   || '';
                    const hasBoth   = fromGroup && fromMap && fromGroup !== fromMap;
                    const hasMapOnly = !fromGroup && fromMap;
                    const applyStyle = (val) => {
                      setBatchBernadoStyle(val);
                      setBatchStyleIsSuggested(true);
                      setBatchQueue(prev => prev.map((entry, i) => i === batchIndex ? { ...entry, bernadoStyle: val, bernadoStyleIsSuggested: true } : entry));
                    };

                    if (hasBoth) return (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          Suggestions — click to apply
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                          <Tooltip title="From existing group record">
                            <Box onClick={() => applyStyle(fromGroup)} sx={{
                              display: 'flex', alignItems: 'center', gap: 0.5,
                              px: 1, py: 0.4, borderRadius: '8px', cursor: 'pointer',
                              border: '1px solid rgba(99,102,241,0.4)', bgcolor: 'rgba(99,102,241,0.08)',
                              '&:hover': { bgcolor: 'rgba(99,102,241,0.16)' },
                            }}>
                              <Typography sx={{ fontSize: '0.72rem', color: '#818cf8', fontWeight: 600 }}>📁 {fromGroup}</Typography>
                              <Typography sx={{ fontSize: '0.63rem', color: 'text.disabled' }}>from group</Typography>
                            </Box>
                          </Tooltip>
                          <Tooltip title="From style/color/size history">
                            <Box onClick={() => applyStyle(fromMap)} sx={{
                              display: 'flex', alignItems: 'center', gap: 0.5,
                              px: 1, py: 0.4, borderRadius: '8px', cursor: 'pointer',
                              border: '1px solid rgba(16,185,129,0.4)', bgcolor: 'rgba(16,185,129,0.08)',
                              '&:hover': { bgcolor: 'rgba(16,185,129,0.16)' },
                            }}>
                              <Typography sx={{ fontSize: '0.72rem', color: '#10b981', fontWeight: 600 }}>🗂 {fromMap}</Typography>
                              <Typography sx={{ fontSize: '0.63rem', color: 'text.disabled' }}>from history</Typography>
                            </Box>
                          </Tooltip>
                        </Box>
                      </Box>
                    );

                    if (hasMapOnly) return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Suggested (style/color/size match):</Typography>
                        <Box onClick={() => applyStyle(fromMap)} sx={{
                          px: 1, py: 0.3, borderRadius: '8px', cursor: 'pointer',
                          border: '1px solid rgba(16,185,129,0.4)', bgcolor: 'rgba(16,185,129,0.08)',
                          '&:hover': { bgcolor: 'rgba(16,185,129,0.16)' },
                        }}>
                          <Typography sx={{ fontSize: '0.75rem', color: '#10b981', fontWeight: 700 }}>{fromMap}</Typography>
                        </Box>
                        <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled' }}>click to apply</Typography>
                      </Box>
                    );

                    return null;
                  })()}

                  {/* Customer Item # */}
                  {batchBernadoStyle.trim() && (() => {
                    const autoSuggest = batchCustomerItem && batchCustomerItem.allow_user_input === false;
                    const userInput   = batchCustomerItem && batchCustomerItem.allow_user_input === true;

                    // Auto-suggest vendors (AllowUserItemNumber=0, e.g. Chico's):
                    // Pre-fill the field value so user just clicks Confirm.
                    if (autoSuggest) {
                      const suggested = batchCustomerItemOverride || batchCustomerItem.customer_item || '';
                      return (
                        <TextField
                          fullWidth
                          label="Customer Item #"
                          value={suggested}
                          onChange={e => setBatchCustomerItemOverride(e.target.value)}
                          InputProps={{
                            endAdornment: (
                              <Box sx={{
                                px: 0.75, py: 0.2, borderRadius: '6px', fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.06em', whiteSpace: 'nowrap',
                                ...(batchCustomerItem.is_new
                                  ? { bgcolor: 'rgba(99,102,241,0.12)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.3)' }
                                  : { bgcolor: 'rgba(16,185,129,0.1)', color: '#10b981', border: '1px solid rgba(16,185,129,0.3)' }),
                              }}>
                                {batchCustomerItem.is_new ? 'NEW' : 'EXISTING'}
                              </Box>
                            ),
                          }}
                          helperText="Auto-generated from i2 — edit if needed"
                          sx={{ mb: 0 }}
                        />
                      );
                    }

                    // Manual-entry vendors (AllowUserItemNumber=1):
                    // If i2 returned an existing customer_item (reorder), show it pre-filled with EXISTING badge.
                    // If no match found, show empty field for the user to type.
                    const hasExisting = userInput && batchCustomerItem?.customer_item;
                    return (
                      <TextField
                        fullWidth
                        label="Customer Item #"
                        value={batchCustomerItemOverride}
                        onChange={e => setBatchCustomerItemOverride(e.target.value)}
                        placeholder={batchCustomerItemLoading ? 'Fetching…' : 'Enter customer item #'}
                        InputProps={{
                          endAdornment: hasExisting && !batchCustomerItemLoading ? (
                            <Box sx={{
                              px: 0.75, py: 0.2, borderRadius: '6px', fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.06em', whiteSpace: 'nowrap',
                              bgcolor: 'rgba(16,185,129,0.1)', color: '#10b981', border: '1px solid rgba(16,185,129,0.3)',
                            }}>
                              EXISTING
                            </Box>
                          ) : null,
                        }}
                        helperText={
                          hasExisting
                            ? 'Found in i2 — edit if needed'
                            : 'This customer provides their own item # — enter it manually'
                        }
                        sx={{ mb: 0 }}
                      />
                    );
                  })()}

                  {/* Inline style image — appears as soon as Bernardo Style # is typed */}
                  {batchBernadoStyle.trim() && (
                    <Box
                      onClick={() => !batchImgLoading && !batchImgNotFound && setBatchImgModalOpen(true)}
                      sx={{
                        height: 130, borderRadius: '10px', overflow: 'hidden',
                        border: '1px solid',
                        borderColor: batchImgNotFound ? 'rgba(245,158,11,0.45)' : 'divider',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: batchImgNotFound ? 'rgba(245,158,11,0.05)' : 'background.default',
                        mt: 0.5,
                        position: 'relative',
                        cursor: batchImgLoading || batchImgNotFound ? 'default' : 'zoom-in',
                        '&:hover .img-zoom-hint': { opacity: 1 },
                      }}
                    >
                      {/* "Not found" state — shown instead of image */}
                      {batchImgNotFound && !batchImgLoading && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75, px: 2 }}>
                          <WarningAmberIcon sx={{ fontSize: 28, color: '#f59e0b' }} />
                          <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#f59e0b', textAlign: 'center' }}>
                            Style # not found
                          </Typography>
                          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', textAlign: 'center' }}>
                            "{debouncedImgStyle}" does not exist in the Bernardo catalog.
                            Double-check the style number before creating the group.
                          </Typography>
                        </Box>
                      )}
                      {batchImgLoading && (
                        <Box sx={{
                          position: 'absolute', inset: 0,
                          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1,
                          bgcolor: 'background.default',
                          zIndex: 2, borderRadius: '10px',
                        }}>
                          <CircularProgress size={26} sx={{ color: '#818cf8' }} />
                          <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
                            Loading image…
                          </Typography>
                        </Box>
                      )}
                      {/* Only render the img once the debounced style is settled — avoids
                          a request on every keystroke and ensures the spinner shows during
                          the full wait (both the 500 ms debounce AND the HTTP transfer). */}
                      {debouncedImgStyle && (
                        <img
                          key={debouncedImgStyle}
                          src={`https://vportal.bernardomfg.com/api/altimage/${encodeURIComponent(debouncedImgStyle)}`}
                          alt={`Style ${debouncedImgStyle}`}
                          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: batchImgLoading || batchImgNotFound ? 'none' : 'block' }}
                          onLoad={() => { setBatchImgLoading(false); setBatchImgNotFound(false); }}
                          onError={() => { setBatchImgLoading(false); setBatchImgNotFound(true); }}
                        />
                      )}
                      {/* Zoom hint overlay — only when image loaded successfully */}
                      {!batchImgLoading && !batchImgNotFound && (
                        <Box className="img-zoom-hint" sx={{
                          position: 'absolute', inset: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          bgcolor: 'rgba(0,0,0,0.32)',
                          opacity: 0, transition: 'opacity 0.18s',
                          borderRadius: '10px',
                          pointerEvents: 'none',
                        }}>
                          <ZoomInIcon sx={{ color: '#fff', fontSize: 36, filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.5))' }} />
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* Fullscreen image modal */}
                  <Dialog
                    open={batchImgModalOpen}
                    onClose={() => setBatchImgModalOpen(false)}
                    maxWidth="md"
                    PaperProps={{ sx: { bgcolor: '#111', borderRadius: 2, p: 0, overflow: 'hidden' } }}
                  >
                    <Box sx={{ position: 'relative' }}>
                      <IconButton
                        onClick={() => setBatchImgModalOpen(false)}
                        size="small"
                        sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2, bgcolor: 'rgba(0,0,0,0.55)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' } }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                      <img
                        src={debouncedImgStyle ? `https://vportal.bernardomfg.com/api/altimage/${encodeURIComponent(debouncedImgStyle)}` : undefined}
                        alt={`Style ${debouncedImgStyle} (full size)`}
                        style={{ display: 'block', maxWidth: '80vw', maxHeight: '80vh', objectFit: 'contain' }}
                      />
                    </Box>
                  </Dialog>
                </Box>

                {/* Items table — configurable columns */}
                {(() => {
                  const includedCount = current.items.filter(i => !batchExcludedIds.has(i.id)).length;
                  const allSelected   = current.items.every(i => !batchExcludedIds.has(i.id));
                  const someSelected  = !allSelected && current.items.some(i => !batchExcludedIds.has(i.id));
                  // Preserve the user's saved order by mapping batchDialogColumns keys → column objects
                  const batchColMap = Object.fromEntries(BATCH_DIALOG_ALL_COLUMNS.map(c => [c.key, c]));
                  const activeCols  = batchDialogColumns.map(k => batchColMap[k]).filter(Boolean);

                  const handleBatchColDrop = (dropKey) => {
                    const dragKey = batchDragColRef.current;
                    if (!dragKey || dragKey === dropKey) return;
                    const cols = [...batchDialogColumns];
                    const from = cols.indexOf(dragKey);
                    const to   = cols.indexOf(dropKey);
                    if (from === -1 || to === -1) return;
                    cols.splice(from, 1);
                    cols.splice(to, 0, dragKey);
                    setBatchDialogColumns(cols);
                    localStorage.setItem('batch_dialog_columns', JSON.stringify(cols));
                  };

                  return (
                    <Box sx={{ px: 2.5, pb: 2 }}>
                      {/* Header row */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'text.secondary' }}>
                          Items in this group
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography sx={{ fontSize: '0.68rem', color: includedCount > 0 ? 'text.secondary' : 'error.main' }}>
                            {includedCount} / {current.items.length} selected
                          </Typography>
                          {/* Column picker button */}
                          <Tooltip title="Configure visible columns">
                            <IconButton
                              size="small"
                              onClick={e => setColPickerAnchor(e.currentTarget)}
                              sx={{
                                p: 0.4, borderRadius: '6px',
                                border: '1px solid', borderColor: 'divider',
                                color: colPickerAnchor ? '#818cf8' : 'text.secondary',
                                bgcolor: colPickerAnchor ? 'rgba(99,102,241,0.08)' : 'transparent',
                                '&:hover': { color: '#818cf8', borderColor: 'rgba(99,102,241,0.4)', bgcolor: 'rgba(99,102,241,0.06)' },
                              }}
                            >
                              <ViewColumnIcon sx={{ fontSize: '0.95rem' }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>

                      {/* Column picker popover */}
                      <Popover
                        open={Boolean(colPickerAnchor)}
                        anchorEl={colPickerAnchor}
                        onClose={() => setColPickerAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        PaperProps={{
                          sx: { borderRadius: '12px', p: 1.5, minWidth: 220, maxHeight: 380, overflowY: 'auto',
                                border: '1px solid', borderColor: 'divider', boxShadow: '0 8px 24px rgba(0,0,0,0.18)' }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                            Columns
                          </Typography>
                          <Button
                            size="small"
                            onClick={() => {
                              setBatchDialogColumns(BATCH_DIALOG_DEFAULT_COLS);
                              localStorage.setItem('batch_dialog_columns', JSON.stringify(BATCH_DIALOG_DEFAULT_COLS));
                            }}
                            sx={{ fontSize: '0.65rem', py: 0, px: 0.75, minWidth: 0, color: 'text.disabled' }}
                          >
                            Reset
                          </Button>
                        </Box>
                        <FormGroup>
                          {/* Active columns first (in current order), then unchecked ones */}
                          {[
                            ...batchDialogColumns.map(k => BATCH_DIALOG_ALL_COLUMNS.find(c => c.key === k)).filter(Boolean),
                            ...BATCH_DIALOG_ALL_COLUMNS.filter(c => !batchDialogColumns.includes(c.key)),
                          ].map(col => (
                            <FormControlLabel
                              key={col.key}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={batchDialogColumns.includes(col.key)}
                                  onChange={e => {
                                    const updated = e.target.checked
                                      ? [...batchDialogColumns, col.key]
                                      : batchDialogColumns.filter(k => k !== col.key);
                                    setBatchDialogColumns(updated);
                                    localStorage.setItem('batch_dialog_columns', JSON.stringify(updated));
                                  }}
                                  sx={{ p: 0.4 }}
                                />
                              }
                              label={<Typography sx={{ fontSize: '0.78rem' }}>{col.label}</Typography>}
                              sx={{ mx: 0, my: 0.1 }}
                            />
                          ))}
                        </FormGroup>
                      </Popover>

                      {/* Items table */}
                      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '8px', maxHeight: 240 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell padding="checkbox" sx={{ py: 0.75, px: 1, bgcolor: 'background.paper' }}>
                                <Checkbox
                                  size="small"
                                  checked={allSelected}
                                  indeterminate={someSelected}
                                  onChange={() => {
                                    if (allSelected) setBatchExcludedIds(new Set(current.items.map(i => i.id)));
                                    else setBatchExcludedIds(new Set());
                                  }}
                                  sx={{ p: 0 }}
                                />
                              </TableCell>
                              {activeCols.map(col => (
                                <TableCell
                                  key={col.key}
                                  draggable
                                  onDragStart={() => { batchDragColRef.current = col.key; }}
                                  onDragOver={e => e.preventDefault()}
                                  onDrop={() => handleBatchColDrop(col.key)}
                                  onDragEnd={() => { batchDragColRef.current = null; }}
                                  sx={{
                                    fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.06em',
                                    textTransform: 'uppercase', py: 0.75, px: 1.5,
                                    bgcolor: 'background.paper', whiteSpace: 'nowrap',
                                    cursor: 'grab', userSelect: 'none',
                                    '&:hover': { bgcolor: 'rgba(99,102,241,0.06)', color: '#818cf8' },
                                    '&:active': { cursor: 'grabbing' },
                                  }}
                                >
                                  {col.label}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {current.items.map((it, i) => {
                              const checked = !batchExcludedIds.has(it.id);
                              return (
                                <TableRow
                                  key={i}
                                  hover
                                  onClick={() => setBatchExcludedIds(prev => {
                                    const n = new Set(prev);
                                    checked ? n.add(it.id) : n.delete(it.id);
                                    return n;
                                  })}
                                  sx={{ cursor: 'pointer', opacity: checked ? 1 : 0.4 }}
                                >
                                  <TableCell padding="checkbox" sx={{ py: 0.75, px: 1 }}>
                                    <Checkbox size="small" checked={checked} onChange={() => {}} sx={{ p: 0 }} />
                                  </TableCell>
                                  {activeCols.map(col => (
                                    <TableCell key={col.key} sx={{ py: 0.75, px: 1.5, fontSize: '0.8rem', whiteSpace: 'nowrap', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {it[col.key] || '—'}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  );
                })()}
              </DialogContent>

              <DialogActions sx={{ px: 2.5, py: 2, gap: 1 }}>
                <Button variant="outlined" onClick={() => setBatchOpen(false)} disabled={batchConfirming}
                  sx={{ borderColor: 'divider', color: 'text.secondary' }}>
                  Cancel All
                </Button>
                <Button variant="outlined" onClick={() => advanceBatch(batchQueue, batchIndex)} disabled={batchConfirming}
                  sx={{ borderColor: 'divider', color: 'text.secondary' }}>
                  Skip
                </Button>
                <Button
                  variant="contained"
                  startIcon={batchConfirming ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <CheckIcon sx={{ fontSize: 16 }} />}
                  disabled={batchConfirming || (!isNew ? false : !batchGroupName.trim()) || current.items.filter(i => !batchExcludedIds.has(i.id)).length === 0}
                  onClick={handleBatchConfirm}
                  sx={{
                    background: 'linear-gradient(135deg, #6366f1, #818cf8)',
                    '&:hover': { background: 'linear-gradient(135deg, #4f46e5, #6366f1)' },
                    fontWeight: 700,
                  }}
                >
                  {batchConfirming ? 'Applying…' : remainingCount === 1 ? 'Confirm & Finish' : 'Confirm & Next →'}
                </Button>
              </DialogActions>
            </Dialog>
          );
        })()}

        {/* ── Add to Existing Group Dialog ── */}
        <Dialog open={atgOpen} onClose={() => !atgSaving && setAtgOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <GroupWorkIcon color="primary" /> Add {selectedCount} Item{selectedCount !== 1 ? 's' : ''} to Existing Group
            </Box>
            <IconButton onClick={() => setAtgOpen(false)} size="small" disabled={atgSaving}><CloseIcon /></IconButton>
          </DialogTitle>
          <DialogContent>
            {atgLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            ) : existingGroups.length === 0 ? (
              <Alert severity="info">No existing groups found for this vendor. Create one first.</Alert>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Select the group to add the selected item{selectedCount !== 1 ? 's' : ''} to:
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                  {existingGroups.map(g => {
                    const isSel = atgSelectedGroup?.id === g.id;
                    const cnt = Array.isArray(g.line_item_ids) ? g.line_item_ids.length : 0;
                    return (
                      <Paper key={g.id} variant="outlined" onClick={() => handleAtgGroupSelect(g)} sx={{
                        p: 1.5, cursor: 'pointer', borderRadius: 2, border: '2px solid',
                        borderColor: isSel ? 'primary.main' : 'divider',
                        bgcolor: isSel ? 'rgba(25,118,210,0.06)' : 'background.paper',
                        '&:hover': { borderColor: 'primary.light' }, transition: 'all 0.15s ease',
                      }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography fontWeight={isSel ? 'bold' : 'normal'}>{g.group_name}</Typography>
                          <Chip label={`${cnt} item${cnt !== 1 ? 's' : ''}`} size="small" color={isSel ? 'primary' : 'default'} />
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          Vendor: {g.vendor ? vendorDisplayName(g.vendor) : 'N/A'} &nbsp;|&nbsp; Created: {g.created_at ? new Date(g.created_at).toLocaleDateString() : 'N/A'}
                        </Typography>
                        {(g.bernardo_style_number || g.customer_item) && (
                          <Box sx={{ display: 'flex', gap: 1.5, mt: 0.4, flexWrap: 'wrap' }}>
                            {g.bernardo_style_number && (
                              <Typography variant="caption" color="text.secondary">
                                Style #: <strong>{g.bernardo_style_number}</strong>
                              </Typography>
                            )}
                            {g.customer_item && (
                              <Typography variant="caption" color="text.secondary">
                                Customer Item #: <strong>{g.customer_item}</strong>
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Paper>
                    );
                  })}
                </Box>
                {atgSelectedGroup && (
                  atgViolations.length === 0 ? (
                    <Alert severity="success">Merge passes all grouping rules ✓</Alert>
                  ) : (
                    <Box>
                      <Alert 
                        severity="error" 
                        sx={{ mb: 1, cursor: 'pointer', alignItems: 'center', '& .MuiAlert-message': { flex: 1 } }} 
                        onClick={() => setShowAtgViolations(!showAtgViolations)}
                        action={showAtgViolations ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      >
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold">Rule Violations After Merge</Typography>
                          <Typography variant="caption" display="block">You cannot proceed until the selection is corrected.</Typography>
                        </Box>
                      </Alert>
                      <Collapse in={showAtgViolations}>
                        {atgViolations.map((v, i) => (
                          <Alert key={i} severity="warning" sx={{ mb: 0.5, py: 0.25 }}>
                            <Typography variant="caption">{v}</Typography>
                          </Alert>
                        ))}
                      </Collapse>
                    </Box>
                  )
                )}
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button onClick={() => setAtgOpen(false)} disabled={atgSaving}>Cancel</Button>
            <Button variant="contained" color="primary" onClick={handleAddToGroup}
              disabled={!atgSelectedGroup || atgSaving || atgViolations.length > 0}
              startIcon={atgSaving ? <CircularProgress size={16} /> : <GroupWorkIcon />}>
              {atgSaving ? 'Adding…' : `Add to "${atgSelectedGroup?.group_name || ''}"`}
            </Button>
          </DialogActions>
        </Dialog>


        {/* Snackbar feedback */}
        <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))} sx={{ width: '100%' }}>
            {snackbar.msg}
          </Alert>
        </Snackbar>

        {/* ── Inline value_in editor ── */}
        <Dialog
          open={!!valueInEditDialog}
          onClose={() => setValueInEditDialog(null)}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: '16px', bgcolor: 'background.paper' } }}
        >
          <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>
                Edit Permitted Values
              </Typography>
              {valueInEditDialog && (
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.25 }}>
                  Rule: <strong>{valueInEditDialog.rule.name}</strong>
                  {valueInEditDialog.rule.field && (
                    <> · Field: <strong>{valueInEditDialog.rule.field}</strong></>
                  )}
                </Typography>
              )}
            </Box>
            <IconButton size="small" onClick={() => setValueInEditDialog(null)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ pt: 0.5 }}>
            {/* Current values list */}
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: 'text.secondary', mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Current values ({valueInEditValues.length})
            </Typography>
            {valueInEditValues.length === 0 ? (
              <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled', fontStyle: 'italic', mb: 1.5 }}>
                No values set — add some below.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, mb: 1.5 }}>
                {valueInEditValues.map((val, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 0.4,
                      px: 0.9, py: 0.3, borderRadius: '8px',
                      border: '1px solid rgba(16,185,129,0.3)',
                      bgcolor: 'rgba(16,185,129,0.08)',
                    }}
                  >
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#10b981' }}>
                      {val}
                    </Typography>
                    <IconButton
                      size="small"
                      sx={{ p: 0, ml: 0.2, color: '#ef4444', '&:hover': { color: '#dc2626' } }}
                      onClick={() => setValueInEditValues(prev => prev.filter((_, i) => i !== idx))}
                    >
                      <CloseIcon sx={{ fontSize: '0.75rem' }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}

            {/* Add new value */}
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: 'text.secondary', mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Add value
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Type a value and press Enter or Add"
                value={valueInEditInput}
                onChange={e => setValueInEditInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const v = valueInEditInput.trim();
                    if (v && !valueInEditValues.includes(v)) {
                      setValueInEditValues(prev => [...prev, v]);
                    }
                    setValueInEditInput('');
                  }
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '0.82rem' } }}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  const v = valueInEditInput.trim();
                  if (v && !valueInEditValues.includes(v)) {
                    setValueInEditValues(prev => [...prev, v]);
                  }
                  setValueInEditInput('');
                }}
                sx={{ borderRadius: '8px', px: 1.5, whiteSpace: 'nowrap', borderColor: '#10b981', color: '#10b981', '&:hover': { borderColor: '#059669', bgcolor: 'rgba(16,185,129,0.08)' } }}
              >
                Add
              </Button>
            </Box>
          </DialogContent>

          <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => setValueInEditDialog(null)}
              sx={{ borderRadius: '8px', fontSize: '0.8rem' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                if (!valueInEditDialog) return;
                const { rsId, rule } = valueInEditDialog;
                const updatedRuleSets = ruleSets.map(rs =>
                  rs.id !== rsId ? rs : {
                    ...rs,
                    rules: rs.rules.map(r =>
                      (r.id || r.name) !== (rule.id || rule.name) ? r : { ...r, values: valueInEditValues }
                    ),
                  }
                );
                const v = vendorRef.current;
                setRuleSets(updatedRuleSets);
                localStorage.setItem(`po_rule_sets_${v}`, JSON.stringify(updatedRuleSets));
                axios.put(`${API}/api/rule-sets/${v}`, { rule_sets: updatedRuleSets })
                  .catch(err => console.error('Failed to save rule sets to DB:', err));
                setValueInEditDialog(null);
              }}
              sx={{
                borderRadius: '8px', fontSize: '0.8rem', fontWeight: 600,
                background: 'linear-gradient(135deg, #10b981, #059669)',
                '&:hover': { background: 'linear-gradient(135deg, #059669, #047857)' },
              }}
            >
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>

        {/* Global Rule Sets Config Dialog */}
        <RuleConfigDialog
          open={ruleConfigOpen}
          onClose={() => setRuleConfigOpen(false)}
          initialRuleSets={ruleSets}
          onSave={(newRuleSets) => {
            const v = RULE_VENDOR_ALIAS[vendorRef.current] || vendorRef.current;
            setRuleSets(newRuleSets);
            // Persist to localStorage (instant cache) and DB (source of truth)
            localStorage.setItem(`po_rule_sets_${v}`, JSON.stringify(newRuleSets));
            axios.put(`${API}/api/rule-sets/${v}`, { rule_sets: newRuleSets })
              .catch(err => console.error('Failed to save rule sets to DB:', err));
          }}
          availableFields={vendorFields}
          vendor={RULE_VENDOR_ALIAS[vendor] || vendor}
        />

        {/* Add Custom Rule Dialog */}
        <Dialog open={addRuleOpen} onClose={() => { setAddRuleOpen(false); setNewRuleField(''); }} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ pb: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', fontFamily: '"DM Sans", sans-serif' }}>Add Custom Rule</Typography>
            <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', mt: 0.5 }}>
              Items must share the same value in the selected field to be grouped together.
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ pt: '12px !important' }}>
            <FormControl fullWidth size="small">
              <InputLabel>Field</InputLabel>
              <Select
                value={newRuleField}
                label="Field"
                onChange={e => setNewRuleField(e.target.value)}
              >
                {FIELDS
                  .filter(f => !FIXED_RULES.some(r => r.field === f.key) && !customRules.some(r => r.field === f.key))
                  .map(f => <MenuItem key={f.key} value={f.key}>{f.label}</MenuItem>)
                }
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
            <Button variant="outlined" size="small" onClick={() => { setAddRuleOpen(false); setNewRuleField(''); }} sx={{ borderColor: 'divider', color: 'text.secondary' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              disabled={!newRuleField}
              onClick={() => {
                const label = FIELDS.find(f => f.key === newRuleField)?.label || newRuleField;
                setCustomRules(prev => [...prev, {
                  id: `custom_${Date.now()}`,
                  type: 'match_all',
                  field: newRuleField,
                  enabled: true,
                  name: `Same ${label}`,
                }]);
                setNewRuleField('');
                setAddRuleOpen(false);
              }}
              sx={{ background: 'linear-gradient(135deg, #6366f1, #818cf8)' }}
            >
              Add Rule
            </Button>
          </DialogActions>
        </Dialog>

        {/* Column Selector Menu */}
        <Menu
          anchorEl={colMenuAnchor}
          open={Boolean(colMenuAnchor)}
          onClose={() => setColMenuAnchor(null)}
          PaperProps={{ sx: { maxHeight: 400, width: 250 } }}
        >
          <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2">Select Columns</Typography>
          </Box>
          {[...visibleFields].sort((a, b) => {
            const la = showGrouped && a.key === '_suggested_group_id' ? 'Group ID'
                     : showGrouped && a.key === '_suggested_group_name' ? 'Group Attributes'
                     : a.label;
            const lb = showGrouped && b.key === '_suggested_group_id' ? 'Group ID'
                     : showGrouped && b.key === '_suggested_group_name' ? 'Group Attributes'
                     : b.label;
            return la.localeCompare(lb);
          }).map(f => {
            const isChecked = selectedColumns.includes(f.key);
            const label = showGrouped && f.key === '_suggested_group_id'   ? 'Group ID'
                        : showGrouped && f.key === '_suggested_group_name' ? 'Group Attributes'
                        : f.label;
            return (
              <MenuItem key={f.key} dense onClick={() => {
                if (isChecked) {
                  setSelectedColumns(selectedColumns.filter(c => c !== f.key));
                } else {
                  setSelectedColumns([...selectedColumns, f.key]);
                }
              }}>
                <Checkbox size="small" checked={isChecked} />
                <ListItemText primary={label} />
              </MenuItem>
            );
          })}
        </Menu>

        {/* Saved Filters Menu */}
        <Menu
          anchorEl={savedFiltersAnchor}
          open={Boolean(savedFiltersAnchor)}
          onClose={() => setSavedFiltersAnchor(null)}
          PaperProps={{ sx: { width: 250 } }}
        >
          <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2">Saved Filter Presets</Typography>
          </Box>
          {savedFilterPresets.length === 0 ? (
            <MenuItem disabled>No saved filters</MenuItem>
          ) : (
            savedFilterPresets.map(preset => (
              <MenuItem key={preset.id} onClick={() => handleLoadFilterPreset(preset)} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">{preset.name}</Typography>
                <IconButton size="small" color="error" onClick={(e) => handleDeleteFilterPreset(preset.id, e)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </MenuItem>
            ))
          )}
        </Menu>

        {/* Save Filter Dialog */}
        <Dialog open={saveFilterDialogOpen} onClose={() => setSaveFilterDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Save Filter Preset</DialogTitle>
          <DialogContent>
            <TextField 
              autoFocus 
              fullWidth 
              size="small" 
              label="Preset Name" 
              value={filterPresetName} 
              onChange={e => setFilterPresetName(e.target.value)} 
              sx={{ mt: 2 }} 
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSaveFilterDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveFilterPreset} disabled={!filterPresetName.trim()}>Save</Button>
          </DialogActions>
        </Dialog>
        {/* Viewed Group Details Dialog */}
        <Dialog open={!!viewedGroup} onClose={() => setViewedGroup(null)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 36, height: 36, borderRadius: '9px', background: 'linear-gradient(135deg,rgba(99,102,241,0.2),rgba(99,102,241,0.08))', border: '1px solid rgba(99,102,241,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <GroupWorkIcon sx={{ fontSize: 18, color: '#818cf8' }} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '1rem', fontFamily: '"DM Sans",sans-serif', lineHeight: 1.2 }}>{viewedGroup?.group_name}</Typography>
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>Group ID: {viewedGroup?.id}</Typography>
              </Box>
            </Box>
            <IconButton onClick={() => setViewedGroup(null)} size="small"><CloseIcon /></IconButton>
          </DialogTitle>

          <DialogContent dividers sx={{ p: 0 }}>
            {/* Grouping Rules */}
            <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'text.secondary', mb: 1 }}>
                Grouping Rules ({(viewedGroup?.custom_rules || FIXED_RULES).filter(r => r.enabled !== false).length} active)
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {(viewedGroup?.custom_rules || FIXED_RULES).map(rule => (
                  <Chip
                    key={rule.id}
                    label={rule.name}
                    size="small"
                    sx={{
                      fontSize: '0.72rem', fontWeight: 600, height: 22,
                      bgcolor: rule.enabled === false ? 'transparent' : 'rgba(99,102,241,0.1)',
                      color: rule.enabled === false ? 'text.disabled' : '#818cf8',
                      border: '1px solid',
                      borderColor: rule.enabled === false ? 'rgba(148,163,184,0.2)' : 'rgba(99,102,241,0.3)',
                      opacity: rule.enabled === false ? 0.5 : 1,
                    }}
                  />
                ))}
              </Box>
            </Box>

            <Divider />

            {/* Line Items Table */}
            <Box sx={{ px: 2.5, pt: 1.5, pb: 0.5 }}>
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'text.secondary', mb: 1 }}>
                Line Items ({(viewedGroup?.group_data || []).length})
              </Typography>
            </Box>
            {(() => {
              const rows = viewedGroup?.group_data || [];
              if (rows.length === 0) return (
                <Typography sx={{ px: 2.5, pb: 2, fontSize: '0.8125rem', color: 'text.disabled', fontStyle: 'italic' }}>No items recorded.</Typography>
              );
              const ignoredCols = new Set(['id','purchase_order_id','item_coordinates','_group_object','_is_grouped','_suggested_groups','_suggested_group_id','_suggested_group_name','_group_name','_isMatch']);
              const cols = Array.from(new Set(rows.flatMap(r => Object.keys(r)))).filter(k => !ignoredCols.has(k));
              const fmtLabel = k => k.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase());
              return (
                <TableContainer sx={{ maxHeight: 360, overflowX: 'auto' }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        {cols.map(c => <TableCell key={c} sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', whiteSpace: 'nowrap', bgcolor: 'background.paper' }}>{fmtLabel(c)}</TableCell>)}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((row, i) => (
                        <TableRow key={i} hover>
                          {cols.map(c => <TableCell key={c} sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{row[c] != null && String(row[c]).trim() !== '' ? String(row[c]) : '—'}</TableCell>)}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              );
            })()}
          </DialogContent>

          <DialogActions sx={{ px: 2.5, py: 2 }}>
            <Button onClick={() => setViewedGroup(null)} variant="outlined" sx={{ borderColor: 'divider', color: 'text.secondary' }}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* ── Group Confirm Dialog ── */}
        {groupConfirmDialog && (
          <Dialog
            open={!!groupConfirmDialog}
            onClose={() => { setGroupConfirmDialog(null); setDialogImgStyle(''); setDialogImgSearched(''); setDialogImgModal(false); }}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: '14px' } }}
          >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{
                  width: 32, height: 32, borderRadius: '8px',
                  background: 'linear-gradient(135deg, rgba(16,185,129,0.2), rgba(16,185,129,0.08))',
                  border: '1px solid rgba(16,185,129,0.35)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <GroupWorkIcon sx={{ fontSize: 16, color: '#34d399' }} />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem', fontFamily: '"DM Sans", sans-serif', lineHeight: 1.2 }}>
                    {groupConfirmDialog.group.group_name}
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.25 }}>
                    {groupConfirmDialog.mode === 'grouped' ? 'This item belongs to this group' : 'This item matches an existing group'}
                  </Typography>
                  {(groupConfirmDialog.group.bernardo_style_number || groupConfirmDialog.group.customer_item) && (
                    <Box sx={{ display: 'flex', gap: 1.5, mt: 0.4, flexWrap: 'wrap' }}>
                      {groupConfirmDialog.group.bernardo_style_number && (
                        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                          Style #: <strong>{groupConfirmDialog.group.bernardo_style_number}</strong>
                        </Typography>
                      )}
                      {groupConfirmDialog.group.customer_item && (
                        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                          Customer Item #: <strong>{groupConfirmDialog.group.customer_item}</strong>
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
              <IconButton onClick={() => { setGroupConfirmDialog(null); setDialogImgStyle(''); setDialogImgSearched(''); setDialogImgModal(false); }} size="small"><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ p: 0 }}>
              {/* Current item being viewed */}
              {groupConfirmDialog.mode === 'existing_suggestion' && (
                <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'text.secondary', mb: 1 }}>
                    Item to add
                  </Typography>
                  <Paper variant="outlined" sx={{
                    p: 1.5, borderRadius: '8px',
                    bgcolor: 'rgba(99,102,241,0.05)',
                    borderColor: 'rgba(99,102,241,0.25)',
                  }}>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      {[
                        { label: 'PO #', key: 'customer_po_number' },
                        { label: 'Style #', key: 'customer_style_number' },
                        { label: 'Size', key: 'size' },
                        { label: 'Color', key: 'color' },
                      ].map(({ label, key }) => (
                        groupConfirmDialog.item[key] ? (
                          <Box key={key}>
                            <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'text.disabled' }}>{label}</Typography>
                            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: '#818cf8' }}>{groupConfirmDialog.item[key]}</Typography>
                          </Box>
                        ) : null
                      ))}
                    </Box>
                  </Paper>
                </Box>
              )}

              {/* Style Image Viewer */}
              <Box sx={{ px: 2.5, pt: 1.5, pb: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'text.secondary', mb: 1 }}>
                  Style Image
                </Typography>
                <Box sx={{ mb: dialogImgSearched ? 1.5 : 0 }}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Bernardo Style #"
                    placeholder="Paste style # — image loads automatically"
                    value={dialogImgStyle}
                    onChange={e => setDialogImgStyle(e.target.value)}
                    onPaste={e => {
                      e.preventDefault();
                      const cleaned = e.clipboardData.getData('text')
                        .replace(/[\n\r\t ​‌‍﻿]/g, '').trim();
                      setDialogImgStyle(cleaned);
                    }}
                  />
                </Box>
                {dialogImgSearched && (
                  <Box
                    onClick={() => setDialogImgModal(true)}
                    sx={{
                      height: 180, display: 'flex', justifyContent: 'center', alignItems: 'center',
                      bgcolor: 'background.default', borderRadius: 1.5, overflow: 'hidden',
                      cursor: 'pointer', border: '1px solid', borderColor: 'divider',
                      '&:hover': { opacity: 0.85 },
                    }}
                  >
                    <img
                      src={`https://vportal.bernardomfg.com/api/altimage/${encodeURIComponent(dialogImgSearched.trim())}`}
                      alt={`Style ${dialogImgSearched}`}
                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                      onError={e => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/300x200.png?text=Image+Not+Found'; }}
                    />
                  </Box>
                )}
              </Box>

              {/* Existing group members */}
              <Box sx={{ px: 2.5, pt: groupConfirmDialog.mode === 'existing_suggestion' ? 0.5 : 2, pb: 2 }}>
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'text.secondary', mb: 1 }}>
                  Group members ({(groupConfirmDialog.group.group_data || []).length})
                </Typography>

                {(groupConfirmDialog.group.group_data || []).length === 0 ? (
                  <Typography sx={{ fontSize: '0.8125rem', color: 'text.disabled', fontStyle: 'italic' }}>No items in this group yet.</Typography>
                ) : (
                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '8px', maxHeight: 260 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          {['PO #', 'Style #', 'Size', 'Color'].map(h => (
                            <TableCell key={h} sx={{
                              fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.06em',
                              textTransform: 'uppercase', py: 0.75, px: 1.5,
                              bgcolor: 'background.paper',
                            }}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(groupConfirmDialog.group.group_data || []).map((gItem, i) => (
                          <TableRow key={i} hover>
                            <TableCell sx={{ py: 0.75, px: 1.5, fontSize: '0.8rem' }}>{gItem.customer_po_number || '—'}</TableCell>
                            <TableCell sx={{ py: 0.75, px: 1.5, fontSize: '0.8rem' }}>{gItem.customer_style_number || '—'}</TableCell>
                            <TableCell sx={{ py: 0.75, px: 1.5, fontSize: '0.8rem' }}>{gItem.size || '—'}</TableCell>
                            <TableCell sx={{ py: 0.75, px: 1.5, fontSize: '0.8rem' }}>{gItem.color || '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </DialogContent>

            <DialogActions sx={{ px: 2.5, py: 2, gap: 1 }}>
              <Button variant="outlined" onClick={() => { setGroupConfirmDialog(null); setDialogImgStyle(''); setDialogImgSearched(''); setDialogImgModal(false); }}
                sx={{ borderColor: 'divider', color: 'text.secondary' }}>
                Close
              </Button>
              {groupConfirmDialog.mode === 'grouped' && (
                <Button variant="outlined" color="primary"
                  onClick={() => { setViewedGroup(groupConfirmDialog.group); setGroupConfirmDialog(null); }}>
                  View Full Details
                </Button>
              )}
              {groupConfirmDialog.mode === 'existing_suggestion' && (
                <Button
                  variant="contained"
                  startIcon={<GroupWorkIcon sx={{ fontSize: 16 }} />}
                  onClick={async () => {
                    const { group, item } = groupConfirmDialog;
                    const existingIds  = Array.isArray(group.line_item_ids) ? group.line_item_ids : [];
                    const existingData = Array.isArray(group.group_data)    ? group.group_data    : [];
                    const closeDialog = () => { setGroupConfirmDialog(null); setDialogImgStyle(''); setDialogImgSearched(''); setDialogImgModal(false); };
                    if (existingIds.includes(item.id)) {
                      closeDialog();
                      return;
                    }
                    try {
                      const updatedGroup = { ...group, line_item_ids: [...existingIds, item.id], group_data: [...existingData, item] };
                      await axios.put(`${API}/api/line-item-groups/${group.id}`, {
                        group_name: group.group_name, line_item_ids: updatedGroup.line_item_ids, group_data: updatedGroup.group_data,
                        bernardo_style_number: group.bernardo_style_number || '',
                      });
                      // Update local state — no re-fetch
                      setFetchedGroups(prev => prev.map(g => g.id === group.id ? updatedGroup : g));
                      const patch = items => items.map(i => i.id !== item.id ? i : {
                        ...i, _is_grouped: true, _group_name: group.group_name, _group_object: updatedGroup,
                        _suggested_group_id: updatedGroup.id ?? '', _suggested_group_name: updatedGroup.group_name ?? '',
                      });
                      setLineItems(patch);
                      setFilteredItems(patch);
                      setTickedIds(prev => { const n = new Set(prev); n.delete(item.id); return n; });
                      setCrossedIds(prev => { const n = new Set(prev); n.delete(item.id); return n; });
                      setSnackbar({ open: true, msg: `Item added to "${group.group_name}"!`, severity: 'success' });
                      closeDialog();
                    } catch (e) {
                      setSnackbar({ open: true, msg: e.response?.data?.detail || 'Failed to add item to group.', severity: 'error' });
                    }
                  }}
                  sx={{
                    background: 'linear-gradient(135deg, #10b981, #34d399)',
                    '&:hover': { background: 'linear-gradient(135deg, #059669, #10b981)' },
                    fontWeight: 700,
                  }}
                >
                  Add to Group
                </Button>
              )}
            </DialogActions>
          </Dialog>
        )}

        {/* Full-size image modal for GroupConfirmDialog */}
        <Dialog open={dialogImgModal} onClose={() => setDialogImgModal(false)} maxWidth="lg" fullWidth>
          <Box sx={{ position: 'relative', bgcolor: '#181c24', p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
            <IconButton onClick={() => setDialogImgModal(false)} sx={{ position: 'absolute', right: 8, top: 8, color: 'white', bgcolor: 'rgba(0,0,0,0.5)' }}>
              <CloseIcon />
            </IconButton>
            <img
              src={`https://vportal.bernardomfg.com/api/altimage/${encodeURIComponent((dialogImgSearched || '').trim())}`}
              alt={`Style ${dialogImgSearched}`}
              style={{ maxWidth: '100%', maxHeight: '85vh', objectFit: 'contain' }}
            />
          </Box>
        </Dialog>

        {/* Suggested Matches Dialog */}
        <Dialog open={!!suggestedItem} onClose={() => setSuggestedItem(null)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><GroupWorkIcon color="info" /> Suggested Group Matches</Box>
            <IconButton onClick={() => setSuggestedItem(null)} size="small" disabled={!!mergingGroupId}><CloseIcon /></IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="text.secondary" paragraph>
              This line item seamlessly fits into the following groups based on their specific configuration rules.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {(suggestedItem?._suggested_groups || []).map(g => (
                <Paper key={g.id} variant="outlined" sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">{g.group_name}</Typography>
                    <Typography variant="caption" color="text.secondary">{g.line_item_ids?.length || 0} existing items</Typography>
                  </Box>
                  <Button 
                    variant="contained" 
                    color="info" 
                    size="small"
                    disabled={!!mergingGroupId}
                    onClick={() => handleMergeSuggestedGroup(g)}
                  >
                    {mergingGroupId === g.id ? <CircularProgress size={20} color="inherit" /> : 'Merge into Group'}
                  </Button>
                </Paper>
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSuggestedItem(null)} color="inherit" disabled={!!mergingGroupId}>Cancel</Button>
          </DialogActions>
        </Dialog>
      </Box>

      {/* ── Leave-page warning dialog ── */}
      <Dialog
        open={blocker.state === 'blocked'}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '14px' },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 38, height: 38, borderRadius: '10px',
                background: 'linear-gradient(135deg, rgba(245,158,11,0.2), rgba(245,158,11,0.08))',
                border: '1px solid rgba(245,158,11,0.35)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}
            >
              <Typography sx={{ fontSize: '1.1rem' }}>⚠️</Typography>
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '1rem', fontFamily: '"DM Sans", sans-serif', lineHeight: 1.2 }}>
                Leave this page?
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', mt: 0.25 }}>
                Your progress will be lost
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          <Alert
            severity="warning"
            sx={{ borderRadius: '8px', fontSize: '0.8125rem' }}
          >
            Your current grouping rules and any fetched data will be cleared when you leave this page.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => blocker.reset()}
            sx={{ borderColor: 'divider', color: 'text.secondary', flex: 1 }}
          >
            Stay on page
          </Button>
          <Button
            variant="contained"
            onClick={() => blocker.proceed()}
            sx={{
              flex: 1,
              background: 'linear-gradient(135deg, #f43f5e, #fb7185)',
              '&:hover': { background: 'linear-gradient(135deg, #e11d48, #f43f5e)' },
            }}
          >
            Leave anyway
          </Button>
        </DialogActions>
      </Dialog>

    </LocalizationProvider>
  );
};

export default LineItemFilterPage;
